import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle2, Star, Menu, X, Instagram, Facebook } from "lucide-react";

// ---------------- Quote Wizard: initial form state (shared) ----------------
// NOTE: This must be module-scoped because it is referenced by the top-level
// CateringWebsite component (useState(initialForm)) and also used for resets.
const initialForm = {
  // Event detail inputs
  serviceStyle: "", // "Plated" | "Buffet"
  bartender: null, // boolean
  drinkService: null, // boolean
  texMexQueso: null, // boolean (Tex-Mex queso add-on)
  // Beverage service selections
  drinkOptions: [], // ["Iced Tea","Sweet Tea","Coffee","Lemonade","Cucumber Water","Lemon Water"]
  drinkSodas: false, // upgrade to include assorted sodas ($4/pp vs $2/pp)
  rentals: null, // boolean
  rentalOptions: [], // ["Plateware","Cutlery","Cocktail Tables","Champagne Glasses","Other"]
  rentalsOther: "",
  kidsMenuDiscuss: false, // boolean
  kidsCount: 10, // min 10 when kids menu selected

  // Contact inputs
  name: "",
  email: "",
  phone: "",
  date: "",
  guests: "",
  venue: "",
  budget: "",
  message: "",
};

// ---------------- Helpers ----------------
// Used in multiple components (some defined outside CateringWebsite),
// so it must be module-scoped to avoid ESLint no-undef.
function isDozenItem(name = "") {
  return /\/Dz\.?/i.test(String(name || ""));
}

// Appetizers: detect special pricing formats so we can show the right selectors.
function isMedLgItem(name = "") {
  const s = String(name || "");
  // e.g. "$60 Med., $85 Lg." or any string containing both "Med" and "Lg"
  return /\bMed\.?\b/i.test(s) && /\bLg\.?\b/i.test(s);
}

function isQtItem(name = "") {
  return /\bQt\.?\b/i.test(String(name || ""));
}

function isGalItem(name = "") {
  return /\bGal\.?\b/i.test(String(name || ""));
}

// Texas Chef Foods Catering - Red/Black Theme + Hero Video Scroller (no Yelp fetch)
// UPDATED (this commit):
// 1) Lunch/Dinner toggle at top of Sample Menus.
// 2) Equalized menu card heights regardless of bullet count.
// 3) "Inquire" button per menu that pre-fills the contact form and smooth-scrolls to it.
// 4) Keeps your existing content/structure; only menus section logic changed plus minor helpers.

const BRAND = {
  name: "Texas Chef Foods Catering",
  phone: "(972) 464-6883",
  phoneHref: "+19724646883",
  email: "Bill@TXChef.com",
  address: "Serving DFW & North Texas Since 2003",
  logo: "/logo.png",
  colors: {
    bg: "#0b0b0c",
    text: "#111111",
    red: "#d62828",
    redDark: "#b41616",
  },
};

// Stripe Payment Link - replace with your real link
const PAY_URL = "https://buy.stripe.com/bJe8wR1tT2ee0fWcrRfw401";

// Google Apps Script Web App (Quote Wizard -> DOCX + Email)
// IMPORTANT: Apps Script ContentService responses typically do NOT include CORS headers.
// We send the POST with `mode: "no-cors"` so the browser will still deliver the request.
// Use your deployed Web App /exec URL here:
const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzAoehnwr3Gb6AFDOrMFuZl40SDx8wtTKuI5qU0J6aIEb8tV0IcRpoN4OjdC19fDt0-jQ/exec";

// Mailchimp audience (Texas Chef Foods) subscribe endpoint (embed form action URL)
const MAILCHIMP_SUBSCRIBE_ACTION =
  "https://txchef.us19.list-manage.com/subscribe/post?u=d23eb3100f08ff84fa697c200&id=44d05db36c&f_id=007b2de7f0";

function splitName(fullName) {
  const s = String(fullName || "").trim().replace(/\s+/g, " ");
  if (!s) return { firstName: "", lastName: "" };
  const parts = s.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}


function isTexMexMenu(titleOrName = "") {
  const t = String(titleOrName || "");
  // Match Tex-Mex with various dash characters
  return /tex\s*[\-\u2010\u2011\u2012\u2013\u2014\u2015\u2212]?\s*mex/i.test(t);
}


// Submits a contact to Mailchimp using a hidden form (avoids CORS issues).
// NOTE: This will silently post to Mailchimp; any success/error page is loaded inside a hidden iframe.
function subscribeToMailchimp({ email, firstName, lastName, phone }) {
  try {
    const safeEmail = String(email || "").trim();
    if (!safeEmail) return;

    // Ensure hidden iframe exists (one-time)
    let iframe = document.getElementById("mc_hidden_iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "mc_hidden_iframe";
      iframe.name = "mc_hidden_iframe";
      iframe.style.display = "none";
      document.body.appendChild(iframe);
    }

    const form = document.createElement("form");
    form.action = MAILCHIMP_SUBSCRIBE_ACTION;
    form.method = "post";
    form.target = "mc_hidden_iframe";
    form.style.display = "none";

    const add = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = String(value || "");
      form.appendChild(input);
    };

    // Mailchimp merge field names
    add("EMAIL", safeEmail);
    add("FNAME", String(firstName || "").trim());
    add("LNAME", String(lastName || "").trim());
    add("PHONE", String(phone || "").trim());

    // Bot trap (keep empty)
    add("b_d23eb3100f08ff84fa697c200_44d05db36c", "");

    document.body.appendChild(form);
    form.submit();
    // Cleanup
    setTimeout(() => {
      try { form.remove(); } catch (e) {}
    }, 0);
  } catch (e) {
    // Silent fail — we don't want to block quote submission if Mailchimp fails
  }
}

// Navigation
const navItems = [
  { href: "#home", label: "Home" },
  { href: "#services", label: "Services" },
  { href: "#menus", label: "Menus" },
  { href: "#gallery", label: "Gallery" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

// Content
const services = [
  { icon: CheckCircle2, title: "Full-Service Catering", text: "From setup to clean-up-staffed buffets, plated meals, and service for weddings, galas, and corporate events." },
  { icon: CheckCircle2, title: "Private Chef Dinner", text: "Elevate your evening with a private chef dinner, crafted just for you in the comfort of your own home or venue." },
  { icon: CheckCircle2, title: "Drop-Off + Setup", text: "Chef-crafted menus delivered hot and ready to serve. Great for home celebrations and casual gatherings." },
  { icon: CheckCircle2, title: "Bar Service", text: "TABC-certified bartenders, curated beer/wine lists, and custom cocktails tailored to your event." },
  { icon: CheckCircle2, title: "Party Rentals", text: "Host in style with our premium party rentals, including napkins, dinnerware & silverware, cocktail tables, and champagne flutes." },
  { icon: CheckCircle2, title: "Grazing Boards & Interactive Stations", text: "Artfully designed charcuterie boards, dessert displays, and themed stations (beef carving station, pasta station, seafood raw bar, and more)." },
];

// ------------------ MENUS DATA ------------------
// Dinner menus are your existing list (copied from your script, unchanged),
// now separated so we can toggle Appetizers vs Dinner.
// Dinner pricing tiers (display at top of Dinner section)
const dinnerPricingTiers = [
  {
    category: "Price Category One",
    rates: [
      "50-99 ppl - $17.99 per person",
      "100-174 ppl - $16.49 per person",
      "175+ ppl - $14.99 per person",
    ],
  },
  {
    category: "Price Category Two",
    rates: [
      "50-99 ppl - $22.99 per person",
      "100-174 ppl - $21.49 per person",
      "175+ ppl - $19.99 per person",
    ],
  },
  {
    category: "Price Category Three",
    rates: [
      "50-99 ppl - $39.99 per person",
      "100-174 ppl - $38.49 per person",
      "175+ ppl - $36.99 per person",
    ],
  },
];

// Dinner menus (priced in title; pick logic in bullets)
const dinnerMenus = [
  // ===== PRICE CATEGORY ONE =====
  {
    name: "Price Category One • Tex—Mex Menu",
    items: [
      "Beef & chicken fajitas with grilled onions and peppers (shrimp available for a slightly higher charge)",
      "Flour and/or white corn tortillas",
      "Toppings included: sour cream, guacamole, cheese",
      "Included appetizer: salsa and chips (queso available for $35.00/gallon)",
      "Pick three sides:",
      "• Spanish rice",
      "• Cilantro white rice",
      "• Caribbean yellow rice with pineapple",
      "• Charro beans",
      "• Refried beans",
      "• Borracha beans",
      "• Elote corn",
      "• Texas Chop House Salad (chopped romaine, cabbage, cilantro, roasted corn & red onion, bacon, two cheeses, tomato, cucumber, bacon ranch dressing)",
      "• Garden salad (choice of two dressings)",
    ],
  },
  {
    name: "Price Category One • BBQ Menu",
    items: [
      "Pick three meats:",
      "• Slow smoked beef brisket",
      "• Grilled marinated chicken breasts",
      "• Smoked turkey breast",
      "• Smoked sausage",
      "• Smoked pulled pork",
      "• Baby back ribs (+$2.00 per person)",
      "Served with BBQ sauce, jalapeños, onions and pickles",
      "Pick three sides:",
      "• Smoky baked beans",
      "• Ranch style beans",
      "• Pecan green beans",
      "• Creamy cole slaw",
      "• Southern potato salad",
      "• Crispy cole slaw",
      "• Macaroni salad",
      "• Garlic mashed potatoes",
      "• Dover roasted rosemary potatoes",
      "• Big Kid macaroni & cheese",
      "• Texas Chop House Salad (bacon ranch dressing)",
      "• Garden salad (choice of two dressings)",
      "Included: dinner rolls and butter",
    ],
  },
  {
    name: "Price Category One • Italian Menu",
    items: [
      "Pick two entrées:",
      "• Beef and Italian sausage lasagna",
      "• Spinach Alfredo lasagna",
      "• Chicken Marsala",
      "• Chicken Picatta",
      "• Parmesan crusted chicken with rich marinara and Alfredo sauces",
      "• Grilled chicken pesto penne pasta",
      "• Shrimp scampi primavera on angel hair (large shrimp, carrots, zucchini, red bell peppers, fresh Italian herbs)",
      "• Pasta Tino (Sweet Italian Sausage tossed with Penne pasta, rich Marinara, mushrooms, baked with Italian cheeses and cream)",
      "• Chicken carbonara (pulled chicken, pancetta, walnuts, basil, spaghetti, creamy parmesan sauce)",
      "• Cheese or chicken stuffed tortellinis with rich marinara and Alfredo sauce",
      "• Four cheese or chicken stuffed raviolis with rich marinara and Alfredo sauce",
      "• Lemon chicken scaloppini (mushrooms, capers, artichokes, light lemon butter sauce)",
      "Included appetizer (choose one):",
      "• Garlic hummus and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "• Spinach and artichoke dip with pita crisps",
      "Pick two sides:",
      "• Caesar salad",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Greek salad",
      "• Caprese salad (fresh tomato, mozzarella, basil, balsamic glaze)",
      "• Balsamic infused roasted asparagus",
      "• Garlic mashed potatoes",
      "• Pecan green beans",
      "• Fresh vegetable medley with Italian herb butter",
      "• Angel hair pasta",
      "Included bread (choose one): garlic toast, garlic bread sticks, or rustic Italian breads with butter",
    ],
  },
  {
    name: "Price Category One • Chef’s Carving Station",
    items: [
      "Chef’s carving station with roast beef, spiral ham and smoked turkey",
      "Served as a hot plate with brown gravy and sauces",
      "Pick three sides:",
      "• Texas Chop House Salad (bacon ranch dressing)",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Black bean, corn and avocado salad",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Garlic mashed potatoes",
      "• English Dover roasted potatoes",
      "• Pecan green beans",
      "• Lemon almond green beans",
      "• Grilled bacon-wrapped green bean or asparagus bundles",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "• Big Kid macaroni & cheese",
      "• Gourmet condiment tray (olives, pickles, marinated vegetables)",
      "• Smoky baked beans",
      "Included: dinner rolls and butter",
    ],
  },
  {
    name: "Price Category One • Islands of the Caribbean",
    items: [
      "Jerk chicken and pork skewers with Jamaican BBQ sauce",
      "Included appetizer: chips and mango salsa",
      "Pick three sides:",
      "• Island fruit bowl",
      "• Cuban rice and black beans",
      "• Spanish rice",
      "• Cilantro white rice",
      "• Caribbean yellow rice with pineapple",
      "• Texas Chop House Salad (bacon ranch dressing)",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Corn, black bean and avocado salsa salad",
      "• Macaroni salad",
      "• Southern potato salad",
      "• Greek pasta salad",
      "• Salad of mixed greens, jicama, tomato and tortilla strips (ranch or cilantro lime vinaigrette)",
      "• Roasted asparagus salad with lemon",
      "Included: dinner rolls and butter",
    ],
  },

  // ===== PRICE CATEGORY TWO =====
  {
    name: "Price Category Two • Peppercorn Smoked Top Sirloin",
    items: [
      "Peppercorn smoked top sirloin",
      "Pick one chicken dish:",
      "• Chicken Marsala",
      "• Chicken Picatta",
      "• Parmesan crusted chicken with rich marinara and Alfredo sauces",
      "Included appetizer (choose one):",
      "• Spinach and artichoke dip and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "Pick three sides:",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Fancy garden salad with red wine vinaigrette",
      "• Hearts of palm and butter lettuce salad with chef’s special dressing",
      "• Greek salad",
      "• Caesar salad",
      "• Caprese salad",
      "• Pecan green beans",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Angel hair pasta",
      "• Garlic mashed potatoes",
      "• Crispy shallot green beans",
      "• Lemon almond green beans",
      "• Fresh vegetable pasta primavera",
      "• English Dover roasted potatoes",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "Included bread: dinner rolls and butter",
    ],
  },
  {
    name: "Price Category Two • Stuffed Pork Tenderloin + Rosemary Grilled Salmon",
    items: [
      "Choose one pork tenderloin:",
      "• Spinach, feta cheese and pine nut stuffed pork tenderloin with sherry shallot sauce",
      "• Smoked bacon-wrapped cream cheese stuffed pork tenderloin",
      "Rosemary grilled salmon",
      "Included appetizer (choose one):",
      "• Spinach and artichoke dip and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "Pick three sides:",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Fancy garden salad with red wine vinaigrette",
      "• Hearts of palm and butter lettuce salad with chef’s special dressing",
      "• Greek salad",
      "• Caesar salad",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Pecan wild rice",
      "• Garlic mashed potatoes",
      "• Pecan green beans",
      "• Crispy shallot green beans",
      "• English Dover roasted potatoes",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "• Wild mushroom couscous",
      "• Lemon almond green beans",
      "• Fresh vegetable pasta primavera",
      "Included bread: dinner rolls and butter",
    ],
  },
  {
    name: "Price Category Two • Brazilian Flame Skewers + Bacon Wrapped Chicken Skewers",
    items: [
      "Brazilian skewered beef and pork tenderloins dancing in flames with chimichurri sauce",
      "Bacon wrapped chicken skewers",
      "Included appetizer (choose one):",
      "• Chips and mango salsa",
      "• Spinach and artichoke dip and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "Pick three sides:",
      "• Island fruit bowl",
      "• Texas Chop House Salad (bacon ranch dressing)",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Hearts of palm and butter lettuce salad with chef’s special dressing",
      "• Greek salad",
      "• Caesar salad",
      "• Caprese salad",
      "• Corn, black bean and avocado salsa salad",
      "• Greek pasta salad",
      "• Roasted asparagus salad with lemon",
      "• Cuban rice and black beans",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Pecan wild rice",
      "• Garlic mashed potatoes",
      "• Pecan green beans",
      "• Grilled baby vegetables with a light Greek butter sauce",
      "• Fettuccini Alfredo",
      "• Lemon almond green beans",
      "• Fresh vegetable pasta primavera",
      "• English Dover roasted potatoes",
      "• Parmesan couscous",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "• Grilled bacon-wrapped green bean or asparagus bundles",
      "Included bread: dinner rolls and honey butter",
    ],
  },

  // ===== PRICE CATEGORY THREE =====
  {
    name: "Price Category Three • Roast Prime Rib",
    items: [
      "Roast prime rib with au jus and creamy horseradish sauce",
      "Pick one chicken dish:",
      "• Chicken Marsala",
      "• Chicken Picatta",
      "• Parmesan crusted chicken with rich marinara and Alfredo sauces",
      "Included appetizer (choose one):",
      "• Spinach and artichoke dip and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "Pick three sides:",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Fancy garden salad with honey balsamic vinaigrette",
      "• Hearts of palm and butter lettuce salad with chef’s special dressing",
      "• Caesar salad",
      "• Caprese salad",
      "• Roasted asparagus salad with lemon",
      "• English roasted Dover potatoes",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Baked potatoes with all the trimmings",
      "• Broccoli hollandaise",
      "• Pecan wild rice",
      "• Garlic mashed potatoes",
      "• Pecan green beans",
      "• Crispy shallot green beans",
      "• Grilled baby vegetables with a light Greek butter sauce",
      "• Lemon almond green beans",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "• Parmesan couscous",
      "• Grilled bacon-wrapped green bean or asparagus bundles",
      "Included bread: dinner rolls and butter",
    ],
  },
  {
    name: "Price Category Three • Filet/Chateaubriand + Jumbo Shrimp/Scallops",
    items: [
      "Choose one steak entrée:",
      "• Pan seared filet mignon with green peppercorn sauce",
      "• Chateaubriand with red wine demi-glace",
      "Choose one seafood entrée:",
      "• Grilled jumbo bacon-wrapped shrimp",
      "• Sea scallops with ginger citrus glaze",
      "Included appetizer (choose one):",
      "• Spinach and artichoke dip and pita chips",
      "• Imported and domestic cheeses and grapes with water crackers (4-tier display)",
      "Pick three sides:",
      "• Field greens (tomato, cucumber, glazed pecans, cranberries, blue cheese, blush wine vinaigrette)",
      "• Fancy garden salad with honey balsamic vinaigrette",
      "• Hearts of palm and butter lettuce salad with chef’s special dressing",
      "• Caesar salad",
      "• Caprese salad",
      "• Roasted asparagus salad with lemon",
      "• Black truffle mashed potatoes",
      "• English roasted Dover potatoes",
      "• Balsamic infused roasted asparagus",
      "• Vegetable medley with cilantro lime butter OR Italian herb butter",
      "• Baked potatoes with all the trimmings",
      "• Broccoli hollandaise",
      "• Pecan wild rice",
      "• Garlic mashed potatoes",
      "• Pecan green beans",
      "• Crispy shallot green beans",
      "• Grilled baby vegetables with a light Greek butter sauce",
      "• Lemon almond green beans",
      "• Roasted butternut, zucchini, red peppers and mushrooms with rosemary garlic",
      "• Parmesan couscous",
      "• Grilled bacon-wrapped green bean or asparagus bundles",
      "Included bread: dinner rolls and butter",
    ],
  },
];

// Appetizers & Desserts menus (priced at top, details in bullet points)
const appetizersMenuss = [
  // ===== Hot Bites & Passed Apps =====
  {
    name: "Crabmeat Stuffed Sherry Mushrooms - $25/Dz.",
    items: [
      "Button mushrooms stuffed with crabmeat",
      "Finished with a sherry-butter glaze",
      "Baked until golden and savory",
    ],
  },
  {
    name: "Sausage Stuffed Mushrooms - $20/Dz.",
    items: [
      "Mushrooms stuffed with seasoned sausage and herbs",
      "Oven baked and served hot",
      "Great for cocktail hour",
    ],
  },
  {
    name: "Spicy Meatballs - $15/Dz.",
    items: [
      "Tender meatballs with a spicy glaze",
      "Served hot, toothpick-ready",
      "Optional: mild-to-hot heat level",
    ],
  },
  {
    name: "Newcastle Brown Ale Meat Balls - $15/Dz.",
    items: [
      "Meatballs simmered in Newcastle brown ale sauce",
      "Rich, savory, slightly sweet finish",
      "Served hot for easy passing",
    ],
  },
  {
    name: "Asian Beef & Chicken Satays w/ Spicy Peanut Sauce - $20/Dz.",
    items: [
      "Skewered beef and chicken, grilled",
      "Spicy peanut dipping sauce included",
      "Served sticking out of a pineapple for an amazing presentation!",
    ],
  },
  {
    name: "Jumbo Shrimp Cocktail - $30/Dz.",
    items: [
      "Chilled jumbo shrimp, peeled & deveined",
      "Classic cocktail presentation",
      "Lemon wedges and cocktail sauce",
    ],
  },
  {
    name: "Snow Crab Claws w/ Cherry Cocktail Sauce - $45/Dz.",
    items: [
      "Chilled snow crab claws",
      "Cherry cocktail sauce for a sweet-savory kick",
      "Elegant seafood-forward bite",
    ],
  },
  {
    name: "Shrimp & Crab Claw Shooters w/ Cherry Cocktail Sauce - $45/Dz.",
    items: [
      "Individual shooters with shrimp + crab claw",
      "Cherry cocktail sauce included",
      "Perfect for upscale receptions",
    ],
  },
  {
    name: "Mini Crab Cakes w/ Aioli Sauce - $35/Dz.",
    items: [
      "Golden mini crab cakes",
      "Served with house aioli",
      "Crisp outside, tender center",
    ],
  },
  {
    name: "Shrimp & Crabmeat Stuffed Fried Jalapeños - $30/Dz.",
    items: [
      "Jalapeños stuffed with shrimp and crabmeat",
      "Lightly fried for crunch",
      "Balanced heat with seafood richness",
    ],
  },
  {
    name: "Grilled Bacon-Wrapped Mini Tenderloins - $30/Dz.",
    items: [
      "Beef tenderloin bites, bacon wrapped",
      "Grilled and finished with a savory glaze",
      "Served hot, steakhouse-style",
    ],
  },
  {
    name: "Grilled Bacon-Wrapped Scallops - $35/Dz.",
    items: [
      "Sea scallops wrapped in bacon",
      "Grilled and lightly glazed",
      "A premium passed appetizer",
    ],
  },
  {
    name: "Chicken or Beef Quesadillas w/ Guacamole & Sour Cream - $25/Dz.",
    items: [
      "Griddled quesadilla triangles",
      "Choice of chicken or beef",
      "Guacamole and sour cream included",
    ],
  },
  {
    name: "Steak, Blue Cheese & Sun-dried Tomato Puff Pastries - $30/Dz.",
    items: [
      "Flaky puff pastry bites",
      "Steak + blue cheese + sun-dried tomato filling",
      "Baked until crisp and golden",
    ],
  },
  {
    name: "Indian Tandoor Chicken Puff Pastries - $30/Dz.",
    items: [
      "Puff pastries filled with tandoor-spiced chicken",
      "Warm aromatic spices, not overly hot",
      "Baked fresh for events",
    ],
  },
  {
    name: "Roasted Vegetable & Goat Cheese Puff Pastries - $25/Dz.",
    items: [
      "Roasted seasonal vegetables",
      "Creamy goat cheese in flaky pastry",
      "Vegetarian-friendly crowd favorite",
    ],
  },
  {
    name: "Bruschetta (Cooked to order on-site only!) - $20/Dz.",
    items: [
      "Toasted bread with a fresh bruschetta topping",
      "Prepared on-site for best texture",
      "Finished with balsamic and fresh herbs",
    ],
  },
  {
    name: "Smoked Salmon Crostini - $25/Dz.",
    items: [
      "Crostini topped with smoked salmon",
      "Creamy spread + fresh garnish",
      "Bright lemon + herbs finish",
    ],
  },
  {
    name: "Piped Canapés with Seafood Toppings - $25/Dz.",
    items: [
      "Assorted canapés piped with savory mousse/spread",
      "Finished with seafood toppings",
      "Elegant bite-size presentation",
    ],
  },
  {
    name: "Orange, Walnut & Goat Cheese Stuffed Baby Endives - $25/Dz.",
    items: [
      "Baby endive spears filled with goat cheese",
      "Orange + walnut for sweet crunch",
      "Naturally gluten-friendly",
    ],
  },
  {
    name: "Melons & Asparagus Wrapped in Prosciutto - $20/Dz.",
    items: [
      "Prosciutto-wrapped melon and asparagus bites",
      "Sweet + salty balance",
      "Served chilled or room temp",
    ],
  },
  {
    name: "Blue Cheese Stuffed Olives Fried in Panko - $15/Dz.",
    items: [
      "Blue cheese stuffed olives",
      "Panko breaded and fried crisp",
      "Perfect for a cocktail party.",
    ],
  },
  {
    name: "Bacon Wrapped Grilled Dates - $20/Dz.",
    items: [
      "Sweet dates wrapped in bacon",
      "Grilled for caramelized finish",
      "A sweet-savory crowd pleaser",
    ],
  },
  {
    name: "Buffalo Chicken Wings w/ Blue Cheese + Carrot/Celery - $20/Dz.",
    items: [
      "Classic buffalo wings",
      "Blue cheese dressing included",
      "Carrot/celery sticks on the side",
    ],
  },
  {
    name: "Chicken Fingers w/ Honey Mustard + BBQ Sauce - $25/Dz.",
    items: [
      "Crispy chicken fingers",
      "Honey mustard and BBQ sauces",
      "Kid-friendly and event-friendly",
    ],
  },
  {
    name: "Fried Pork & Vegetable Pot Stickers w/ Teriyaki Dip - $20/Dz.",
    items: [
      "Crispy pot stickers with pork + vegetables",
      "Teriyaki dipping sauce included",
      "Served hot for best texture",
    ],
  },
  {
    name: "Loaded Potato Skins w/ Sour Cream - $18/Dz.",
    items: [
      "Crispy potato skins loaded with toppings",
      "Sour cream included",
      "Comfort-food favorite",
    ],
  },

  // ===== Dips, Boards & Displays =====
  {
    name: "Imported & Domestic Cheeses & Grapes Tray w/ Water Crackers - $60 Med., $85 Lg.",
    items: [
      "Assorted imported + domestic cheeses",
      "Fresh grapes and water crackers",
      "Great for grazing tables",
    ],
  },
  {
    name: "Charcuterie Board / Antipasto Platter - $95 Med., $105 Lg.",
    items: [
      "Cured meats + artisan cheeses",
      "Marinated vegetables, olives, and accompaniments",
      "Crackers/bread as available",
    ],
  },
  {
    name: "Combination 4-Tier on Wine Crates Display - Call for price per person",
    items: [
      "Charcuterie + crudité + cheese & grapes + fresh fruit",
      "Dips, nuts, crackers display styling",
      "Designed as a centerpiece grazing station",
    ],
  },
  {
    name: "Fresh Crudité / Vegetable Platter w/ Ranch - $35 Med., $50 Lg.",
    items: [
      "Fresh seasonal vegetables",
      "House ranch dipping sauce",
      "Bright, crisp, and easy to serve",
    ],
  },
  {
    name: "Fresh Fruit Melon Boats - $50 Med., $75 Lg.",
    items: [
      "Fresh-cut seasonal fruit",
      "Melon boats for a premium presentation",
      "Perfect for daytime or brunch events",
    ],
  },
  {
    name: "Assorted Nuts - $20/Qt.",
    items: [
      "Assorted nuts, lightly salted",
      "Great add-on for grazing tables",
      "Served in bowls or cups as needed",
    ],
  },
  {
    name: "Chips & Salsa - $35 Med., $45 Lg.",
    items: [
      "Tortilla chips with house salsa",
      "Great add-on for casual events",
      "Optional: mild/medium/hot salsa",
    ],
  },
  {
    name: "Queso Dip (w/ sausage or without) - $35 Gal.",
    items: [
      "Creamy queso dip",
      "Choice: plain or sausage",
      "Served hot with chips",
    ],
  },
  {
    name: "Spinach & Artichoke Dip in Bread Bowl - $55 Med., $75 Lg.",
    items: [
      "Creamy spinach-artichoke dip",
      "Served in a warm bread bowl",
      "Extra bread/chips available as needed",
    ],
  },
  {
    name: "Hot Crab Dip w/ Pita Crisps/Chips - $65 Med., $90 Lg.",
    items: [
      "Hot crab dip served warm",
      "Pita crisps and/or chips included",
      "Rich, creamy, and crowd-favorite",
    ],
  },
  {
    name: "Garlic Hummus & Pita Chips - $40 Med., $65 Lg.",
    items: [
      "Garlic hummus with pita chips",
      "Optional veggie add-on upon request",
      "Great for lighter grazing",
    ],
  },

  // ===== Sliders & Sandwiches =====
  {
    name: "Mini Grilled Sausage & Roasted Pepper Sandwiches w/ Pepper Jack - $25/Dz.",
    items: [
      "Grilled sausage + roasted peppers",
      "Pepper jack cheese on mini buns",
      "Warm, hearty bite",
    ],
  },
  {
    name: "Cheeseburger Sliders - $25/Dz.",
    items: [
      "Classic cheeseburger sliders",
      "Pickles/condiments as available",
      "Great for game day or casual events",
    ],
  },
  {
    name: "BBQ Brisket Sliders - $25/Dz.",
    items: [
      "Chopped brisket with BBQ sauce",
      "Mini buns, served warm",
      "Pickles/onions optional as available",
    ],
  },
  {
    name: "Pulled Pork Sliders - $25/Dz.",
    items: [
      "Pulled pork, slow-cooked and sauced",
      "Mini buns, served warm",
      "Optional slaw add-on as available",
    ],
  },
  {
    name: "Buffalo Chicken Sliders - $25/Dz.",
    items: [
      "Buffalo chicken on mini buns",
      "Creamy dressing as available",
      "Perfect for parties and tailgates",
    ],
  },
  {
    name: "Finger Sandwiches (Tuna + Chicken Salad) on Light Breads - $20/Dz.",
    items: [
      "Assorted finger sandwiches",
      "Tuna salad + chicken salad",
      "Light breads for a classic tea-style spread",
    ],
  },
  {
    name: "Ritz Carlton Finger Sandwich Display - $18/Dz.",
    items: [
      "English cucumber",
      "Cadillac pimento cheese",
      "Chicken / cranberry / walnut",
    ],
  },

  // ===== Italian & Skewers =====
  {
    name: "Abruzzi Bread - $20/Dz.",
    items: [
      "Spinach, provolone, pepperoni & sausage rolled and baked",
      "Served with marinara sauce",
      "Warm, shareable, and filling",
    ],
  },
  {
    name: "Crispy Cheese Fried Raviolis w/ Alfredo Sauce - $20/Dz.",
    items: [
      "Crispy fried raviolis",
      "Alfredo dipping sauce included",
      "Comfort food favorite",
    ],
  },
  {
    name: "Antipasto Skewers - $25/Dz.",
    items: [
      "Pepperoni, salami, mozzarella ball, cherry tomato, olive, cheese tortellini",
      "Perfect for grazing tables",
    ],
  },
  {
    name: "Caprese Salad Skewers - $20/Dz.",
    items: [
      "Cherry tomatoes, roasted red pepper, mozzarella ball, basil leaf",
      "Finished with balsamic drizzle.",
      "Fresh, bright, and colorful",
    ],
  },
  {
    name: "Watermelon, Feta Skewers w/ Balsamic Glaze (in season) - $20/Dz.",
    items: [
      "Watermelon + feta skewers",
      "Finished with balsamic glaze",
      "Seasonal favorite (summer)",
    ],
  },

  // ===== Cold/Canapé Bites =====
  {
    name: "Stuffed Cucumber Cups - $18/Dz.",
    items: [
      "Walnut, blue cheese, cream cheese & chives filling",
      "Cucumber cups for a clean bite",
      "Great lighter option",
    ],
  },
  {
    name: "Toasted Phyllo Cups w/ Hot Pepper Jelly, Brie, Pecan + Sea Salt - $20/Dz.",
    items: [
      "Crisp toasted phyllo cups",
      "Hot pepper jelly + brie + pecan",
      "Sea salt dusting to finish",
    ],
  },
  {
    name: "Cheese Flights - $95 Med., $135 Lg.",
    items: [
      "Various imported cheeses",
      "Slices of pears, apples, and figs",
      "Served with water crackers",
    ],
  },

  // ===== Specialty / Bars =====
  {
    name: "Mashed Potato Martini Bar - Call for pricing",
    items: [
      "Creamy mashed potatoes served in martini cups",
      "Topping bar options vary by event",
      "Great interactive station",
    ],
  },
];

const dessertsMenus = [

  {
    name: "Lemon Bars - $20/Dz.",
    items: [
      "Bright lemon filling with buttery crust",
      "Cut into bite-size bars",
      "Perfect for dessert tables",
    ],
  },
  {
    name: "Dessert Shooters - $25/Dz.",
    items: [
      "Flavored chocolate mousse",
      "Strawberry cream pie",
      "Key lime pie mousse with toppings",
    ],
  },
  {
    name: "Chocolate Dipped Strawberries - $22/Dz.",
    items: [
      "Fresh strawberries dipped in chocolate",
      "Finished with decorative drizzle as available",
      "Chilled for best presentation",
    ],
  },
  {
    name: "Chocolate Ganache & Lemon Curd Tarts - $20/Dz.",
    items: [
      "Assorted mini tarts",
      "Chocolate ganache + lemon curd variety",
      "Clean bite-size finish",
    ],
  },
  {
    name: "Mini Cheesecakes Sampler - $18.00/Dz.",
    items: [
      "Assorted mini cheesecake bites",
      "Chef’s selection of flavors",
      "Great for dessert assortments",
    ],
  },
  {
    name: "Assorted Cookies - $18.00/Dz.",
    items: [
      "Assorted classic cookies",
      "Baked fresh as available",
      "Great add-on dessert option",
    ],
  },
  {
    name: "Assorted Brownies - $20.00/Dz.",
    items: [
      "Assorted brownies (classic + chef’s variety)",
      "Rich and fudgy",
      "Cut into bite-size pieces",
    ],
  },
  {
    name: "Assorted Pies & Ice Cream - $2.50 per person",
    items: [
      "Assorted pie slices",
      "Ice cream served alongside",
      "Flavor selection varies by event",
    ],
  },
  {
    name: "Assorted Cheesecake Flavors - $3.50 per person",
    items: [
      "Assorted cheesecake flavors",
      "Plated or buffet service",
      "Seasonal options available",
    ],
  },
  {
    name: "Bread Pudding w/ Whiskey Sauce - $3.50 per person",
    items: [
      "Warm bread pudding",
      "Whiskey sauce drizzle",
      "Comforting southern-style dessert",
    ],
  },
  {
    name: "Assorted Cupcakes - $3.00 per person",
    items: [
      "Assorted cupcake flavors",
      "Frosting and toppings vary",
      "Easy grab-and-go dessert",
    ],
  },
  {
    name: "Sopapilla Cheesecake - $3.50 per person",
    items: [
      "Cinnamon-sugar sopapilla style crust",
      "Creamy cheesecake center",
      "Served in squares for easy service",
    ],
  },
  {
    name: "Cherry Enchiladas - $3.50 per person",
    items: [
      "Sweet cherry-filled dessert enchiladas",
      "Served warm as available",
      "Great plated dessert option",
    ],
  },
];


// Packages (unchanged)
const packages = [
  { title: "Social Buffet", price: "$29", per: "/guest", bullets: ["2 entrées", "2 sides", "1 salad", "Drop-off & setup"], highlight: false },
  { title: "Signature Plated", price: "$55", per: "/guest", bullets: ["2 courses + dessert", "Coffee & tea service", "China, flatware, linens", "On-site chef & staff"], highlight: true },
  { title: "Premium Dinner", price: "$79", per: "/guest", bullets: ["Passed hors d’oeuvres", "3-course plated", "Late-night bites", "Full service team"], highlight: false },
];

const testimonialsFallback = [
  { name: "Danielle R.", quote: "Texas Chef Foods absolutely nailed our 180-person wedding. Every course came out hot and perfect, and the service was seamless." },
  { name: "Marcus P. - HR Director", quote: "We use them monthly for executive lunches. Consistently on-time, professional, and delicious. Zero stress for our team." },
  { name: "Priya K.", quote: "They created a fully vegetarian menu that even our meat-eaters loved. Beautiful presentation and incredible flavors!" },
];

const faqs = [
  { q: "Do you accommodate dietary restrictions?", a: "Yes. We routinely handle gluten-free, dairy-free, vegetarian/vegan, nut-free and other needs. We label everything clearly." },
  { q: "What areas do you serve?", a: "Based in DFW, we travel across North Texas and beyond for larger events. Please reach out to discuss your party location!" },
  { q: "Do you provide tastings?", a: "Absolutely. After an initial consultation and quote, we’ll schedule a private tasting for your selected items." },
];

// Hero videos (local video prioritized)
const heroVideos = [
  {
    local: true,
    srcs: ["/videos/txchef-hero1.mp4", "/videos/txchef-hero1.webm"],
    poster: "/videos/txchef-hero1.jpg",
    title: "Texas Chef Foods - Hero",
  },
];

if (typeof window !== "undefined") {
  console.assert(Array.isArray(navItems) && navItems.length > 0, "navItems must exist");
  console.assert(Array.isArray(heroVideos) && heroVideos.length >= 1, "heroVideos must have at least one item");
}

function Section({ id, children, intent = "light" }) {
  const darker = intent === "dark";
  return (
    <section id={id} className={`scroll-mt-28 py-24 md:py-32 ${darker ? "bg-neutral-950 text-neutral-50" : ""}`}>
      {/* Divider */}
      <div className={`mx-auto max-w-7xl px-4 ${darker ? "border-neutral-800" : "border-neutral-200"}`}>
        <div className="border-t opacity-60 my-10 md:my-16"></div>
      </div>
      {children}
    </section>
  );
}

function HeroVideoScroller({ onRequestQuote }) {
  const [current] = useState(0); // single hero video shown
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const playAttempt = v.play?.();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {});
    }
  }, []);

  const active = heroVideos[current];
  const poster = active.poster || (active.local ? "/videos/txchef-hero1.jpg" : undefined);

  return (
    <div className="relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 pt-12 md:pt-20 grid md:grid-cols-12 gap-10 items-center">
        <motion.div className="md:col-span-7" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-neutral-800 bg-black">
            <video
              ref={videoRef}
              key={current}
              poster={poster}
              className="w-full h-[420px] md:h-[640px] object-cover"
              muted
              loop
              autoPlay
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
            >
              {active.local && active.srcs?.map((s) => (
                <source key={s} src={s} type={s.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
              ))}
            </video>
          </div>
        </motion.div>
        <motion.div className="md:col-span-5 text-neutral-50" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight">
            Chef-Driven Catering for <span className="text-red-500">Unforgettable</span> Events
          </h1>
          <p className="mt-5 text-neutral-300 text-lg">
            Weddings, corporate gatherings, and private dinners-crafted with seasonal ingredients, seamless service, and polished presentation.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#menus" className="rounded-xl bg-red-600 text-white px-6 py-3 hover:bg-red-700 transition">Explore Menus</a>
            <button
              type="button"
              onClick={onRequestQuote}
              className="rounded-xl border border-red-600 text-red-600 px-6 py-3 hover:bg-red-600 hover:text-white transition"
            >
              Request a Quote
            </button>
          </div>
          <div className="mt-6 flex items-center gap-2 text-red-500">
            {[...Array(5)].map((_, i) => (<Star key={i} className="w-5 h-5 fill-current" />))}
            <span className="text-sm text-neutral-300">Rated 5.0 by 300+ clients</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/** Helper: strip tier prefix like "Collection One • " from dinner menu names */
function dinnerTitleFromName(name = "") {
  return String(name).split("•").slice(1).join("•").trim() || String(name).trim();
}


// --- Quote Wizard UI helpers (defined outside the modal to preserve input focus) ---
function QuoteStepShell({
  onClose,
  title,
  subtitle,
  children,
  backText = "Back",
  nextText = "Next",
  onBack,
  onNext,
  nextDisabled,
  bodyRef,
}) {
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden">
          <div className="px-5 sm:px-7 py-4 border-b border-neutral-200 flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-red-600">Event Builder</div>
              <h3 className="text-xl sm:text-2xl font-bold text-neutral-900">{title}</h3>
              {subtitle ? <p className="mt-1 text-sm text-neutral-600">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl border border-neutral-200 px-3 py-2 hover:bg-neutral-50"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={bodyRef} className="max-h-[70vh] overflow-auto px-5 sm:px-7 py-5">
            {children}
          </div>

          <div className="px-5 sm:px-7 py-4 border-t border-neutral-200 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl border border-neutral-200 px-4 py-2 hover:bg-neutral-50"
            >
              {backText}
            </button>

            <button
              type="button"
              onClick={onNext}
              disabled={!!nextDisabled}
              className={`rounded-xl px-5 py-2 font-semibold transition ${
                nextDisabled
                  ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {nextText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuotePillYesNo({ value, onYes, onNo }) {
  return (
    <div className="mt-2 flex flex-wrap gap-3">
      <button
        type="button"
        onClick={onYes}
        className={`rounded-xl px-5 py-2 font-semibold transition ${
          value === true
            ? "bg-red-600 text-white"
            : "border border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
        }`}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={onNo}
        className={`rounded-xl px-5 py-2 font-semibold transition ${
          value === false
            ? "bg-neutral-900 text-white"
            : "border border-neutral-300 text-neutral-800 hover:bg-neutral-100"
        }`}
      >
        No
      </button>
    </div>
  );
}

function QuoteMenuSelectCard({ title, subtitle, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full rounded-2xl border p-5 transition ${
        selected
          ? "border-red-600 ring-2 ring-red-200 bg-white"
          : "border-neutral-200 bg-white hover:bg-neutral-50"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-lg font-semibold text-neutral-900">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-neutral-600">{subtitle}</div> : null}
        </div>
        <div
          className={`shrink-0 rounded-xl px-3 py-2 text-sm font-semibold ${
            selected ? "bg-red-600 text-white" : "border border-red-600 text-red-600"
          }`}
        >
          {selected ? "Selected" : "Select"}
        </div>
      </div>
    </button>
  );
}

function QuoteWizardModal({
  open,
  onClose,
  step,
  setStep,
  form,
  setForm,
  wantsApps,
  setWantsApps,
  wantsDinner,
  setWantsDinner,
  wantsDesserts,
  setWantsDesserts,
  appetizersMenuss,
  dessertsMenus,
  dinnerPricingTiers,
  dinnerMenus,
  dinnerTier,
  setDinnerTier,
  dinnerMenuName,
  setDinnerMenuName,
  dinnerConfig,
  dinnerChoices,
  onToggleApp,
  onToggleDessert,
  selectedApps,
  appQty,
  appSize,
  onSetAppQty,
  onSetAppSize,
  dessertQty,
  onSetDessertQty,
  isSubmitting,
  selectedDesserts,
  onDinnerChoice,
  onSubmit,
}) {
const [missingDinner, setMissingDinner] = useState([]); // array of group keys missing selections
const [drinkLimitMsg, setDrinkLimitMsg] = useState("");
const tierRef = useRef(null);
const menuRef = useRef(null);
const quesoRef = useRef(null);
const dinnerGroupRefs = useRef({});

  const scrollRef = useRef(null);
useEffect(() => {
  // clear dinner missing highlights when menu changes
  setMissingDinner([]);
}, [dinnerTier, dinnerMenuName]);

useEffect(() => {
  // reset texMexQueso when leaving Tex-Mex
  const isTexMex = isTexMexMenu(dinnerMenuName || "") || isTexMexMenu(dinnerConfig?.title || "");
  if (!isTexMex && form?.texMexQueso !== null) {
    setForm((f) => ({ ...f, texMexQueso: null }));
  }
}, [dinnerMenuName, dinnerConfig, setForm]);



  if (!open) return null;

function preserveScroll(fn) {
  const st = scrollRef.current ? scrollRef.current.scrollTop : null;
  fn();
  // restore after React paints
  requestAnimationFrame(() => {
    if (st !== null && scrollRef.current) scrollRef.current.scrollTop = st;
  });
}

const toggleApp = (name) => preserveScroll(() => onToggleApp(name));
const toggleDessert = (name) => preserveScroll(() => onToggleDessert(name));
const setDessertQtySafe = (name, qty) => preserveScroll(() => onSetDessertQty(name, qty));
const chooseDinner = (groupKey, option, limit) => preserveScroll(() => onDinnerChoice(groupKey, option, limit));

  const hasDrinkSelection =
    form.drinkService === false ||
    (form.drinkService === true && Array.isArray(form.drinkOptions) && form.drinkOptions.length > 0);

  const hasRentalSelection =
    form.rentals === false ||
    (form.rentals === true && Array.isArray(form.rentalOptions) && form.rentalOptions.length > 0);

  const canNextFromService =
    (form.serviceStyle || "").trim() &&
    form.bartender !== null &&
    form.drinkService !== null &&
    hasDrinkSelection &&
    form.rentals !== null &&
    hasRentalSelection;

  const canNextFromContact =
    (form.name || "").trim() &&
    (form.email || "").trim() &&
    (form.phone || "").trim() &&
    (form.date || "").trim() &&
    (form.guests || "").trim() &&
    (form.venue || "").trim() &&
    (form.budget || "").trim();

  if (step === 3) {
    return (
      <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
        title="Service Details"
        subtitle="A few quick service questions so we can quote accurately."
        backText="Back"
        nextText="Next"
        onBack={() => setStep(2)}
        onNext={() => setStep(4)}
        nextDisabled={!canNextFromService}
      >
        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-200 bg-white p-5">
            <div className="text-sm font-semibold text-neutral-900">Service Details</div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-neutral-800">Do you want this party plated or buffet style?</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { label: "Plated", value: "Plated" },
                  { label: "Buffet", value: "Buffet" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, serviceStyle: opt.value }))}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      form.serviceStyle === opt.value
                        ? "bg-red-600 text-white"
                        : "border border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-neutral-800">Bartender</div>
              <div className="mt-2">
                <QuotePillYesNo
                  value={form.bartender}
                  onYes={() => setForm((prev) => ({ ...prev, bartender: true }))}
                  onNo={() => setForm((prev) => ({ ...prev, bartender: false }))}
                />
              </div>
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-neutral-800">Drink service</div>
              <div className="mt-2">
                <QuotePillYesNo
                  value={form.drinkService}
                  onYes={() => setForm((prev) => ({ ...prev, drinkService: true }))}
                  onNo={() => setForm((prev) => ({ ...prev, drinkService: false, drinkOptions: [], drinkSodas: false }))}
                />
              </div>
              {form.drinkService === true ? (
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <div className="sm:col-span-2 text-xs text-neutral-600">
                    Select up to three drinks. If you add assorted sodas, that counts as a fourth drink.
                  </div>
                  {drinkLimitMsg ? (
                    <div className="sm:col-span-2 text-xs text-rose-600">{drinkLimitMsg}</div>
                  ) : null}
                  {["Iced Tea", "Sweet Tea", "Coffee", "Lemonade", "Cucumber Water", "Lemon Water"].map((d) => {
                    const checked = Array.isArray(form.drinkOptions) && form.drinkOptions.includes(d);
                    return (
                      <label key={d} className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setDrinkLimitMsg("");
                            setForm((prev) => {
                              const cur = Array.isArray(prev.drinkOptions) ? prev.drinkOptions : [];
                              if (!checked && cur.length >= 3) {
                                setDrinkLimitMsg("Please select up to three drinks. If you add assorted sodas, that counts as a fourth drink.");
                                return prev;
                              }
                              const next = checked ? cur.filter((x) => x !== d) : [...cur, d];
                              return { ...prev, drinkOptions: next };
                            });
                          }}
                        />
                        <span className="text-neutral-800">{d}</span>
                      </label>
                    );
                  })}

                  <label className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={!!form.drinkSodas}
                      onChange={(e) => setForm((prev) => ({ ...prev, drinkSodas: !!e.target.checked }))}
                    />
                    <span className="text-neutral-800">
                      Assorted Sodas (upgrade beverage service to <strong>$4.00</strong> per person)
                    </span>
                  </label>

                  <div className="text-xs text-neutral-600 sm:col-span-2">
                    Beverage service includes cups, ice, sweeteners, stirrers, and creamers.
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-neutral-800">Party rentals</div>
              <div className="mt-2">
                <QuotePillYesNo
                  value={form.rentals}
                  onYes={() => setForm((prev) => ({ ...prev, rentals: true }))}
                  onNo={() => setForm((prev) => ({ ...prev, rentals: false, rentalOptions: [], rentalsOther: "" }))}
                />
              </div>
              {form.rentals === true ? (
                <div className="mt-3 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {["Linen Napkins (White) - $0.95 each", " Linen Napkins (Ivory) - $0.95 each", '10" White China Dinner Plate + Stainless Silverware - $1.75/person', "Cocktail Tables", "Champagne Glasses", "Other"].map((r) => {
                      const checked = Array.isArray(form.rentalOptions) && form.rentalOptions.includes(r);
                      return (
                        <label key={r} className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((prev) => {
                                const cur = Array.isArray(prev.rentalOptions) ? prev.rentalOptions : [];
                                const next = checked ? cur.filter((x) => x !== r) : [...cur, r];
                                const otherCleared = r === "Other" && checked;
                                return { ...prev, rentalOptions: next, rentalsOther: otherCleared ? "" : prev.rentalsOther };
                              });
                            }}
                          />
                          <span className="text-neutral-800">{r}</span>
                        </label>
                      );
                    })}
                  </div>
                  {Array.isArray(form.rentalOptions) && form.rentalOptions.includes("Other") ? (
                    <div>
                      <label className="block text-sm mb-1">Other rentals (optional)</label>
                      <input
                        value={form.rentalsOther}
                        onChange={(e) => setForm((prev) => ({ ...prev, rentalsOther: e.target.value }))}
                        className="w-full rounded-xl border px-3 py-2"
                        placeholder="Tell us what you need"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold text-neutral-800">Kids Menu</div>
              <div className="mt-2 flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3">
                <input
                  type="checkbox"
                  checked={form.kidsMenuDiscuss === true}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      kidsMenuDiscuss: !!e.target.checked,
                      kidsCount: Math.max(10, Number(prev.kidsCount || 10)),
                    }))
                  }
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-neutral-900">Add Kids Menu ($8.50 per kid)</div>
                  <div className="text-xs text-neutral-600 mt-1">
                    Chicken tenders (breaded or grilled) with BBQ + ranch sauces, mac and cheese, fresh fruit bowl, and rolls. Minimum 10 kids.
                  </div>
                </div>
              </div>

              {form.kidsMenuDiscuss === true ? (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-neutral-800">Number of kids (min 10)</label>
                  <input
                    type="number"
                    min={10}
                    value={Number(form.kidsCount || 10)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        kidsCount: Math.max(10, Number(e.target.value || 10)),
                      }))
                    }
                    className="mt-1 w-40 rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </QuoteStepShell>
    );
  }

  if (step === 4) {
    return (
      <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
        title="Event Details"
        subtitle="Start with the basics—this is what we use to build your quote. If you are undecided please estimate to the best of your ability."
        backText="Back"
        nextText="Next"
        onBack={() => setStep(3)}
        onNext={() => setStep(5)}
        nextDisabled={!canNextFromContact}
      >
        <div className="space-y-6">
          <div className="rounded-2xl border border-neutral-200 bg-white p-5">
            <div className="text-sm font-semibold text-neutral-900">Contact Details</div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Name</label>
            <input required value={form.name} onChange={(e)=>setForm((prev)=>({ ...prev, name: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-sm mb-1">Email</label>
            <input type="email" required value={form.email} onChange={(e)=>setForm((prev)=>({ ...prev, email: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="you@example.com" />
          </div>
          <div>
            <label className="block text-sm mb-1">Phone</label>
            <input type="tel" required value={form.phone} onChange={(e)=>setForm((prev)=>({ ...prev, phone: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="(###) ###-####" />
          </div>
          <div>
            <label className="block text-sm mb-1">Event Date</label>
            <input type="date" required value={form.date} onChange={(e)=>setForm((prev)=>({ ...prev, date: e.target.value }))} className="w-full rounded-xl border px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm mb-1">Guest Count</label>
            <input type="number" min="1" required value={form.guests} onChange={(e)=>setForm((prev)=>({ ...prev, guests: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="e.g., 150" />
          </div>
          <div>
            <label className="block text-sm mb-1">Venue Location</label>
            <input required value={form.venue} onChange={(e)=>setForm((prev)=>({ ...prev, venue: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Venue name & city" />
          </div>
          <div>
            <label className="block text-sm mb-1">Party Budget</label>
            <input required value={form.budget} onChange={(e)=>setForm((prev)=>({ ...prev, budget: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="$2,000 - $15,000+" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Notes (optional)</label>
            <textarea rows={3} value={form.message} onChange={(e)=>setForm((prev)=>({ ...prev, message: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Dietary needs, timing, service style, etc." />
          </div>
            </div>
          </div>
        </div>
      </QuoteStepShell>
    );
  }

  if (step === 0) {
    return (
      <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
        title="Appetizers"
        subtitle="Do you want appetizers for your party?"
        backText="Cancel"
        onBack={onClose}
        onNext={() => setStep(1)}
        nextDisabled={wantsApps === null}
      >
        <p className="text-sm text-neutral-700">
          Appetizers may be added to any wedding reception menu in any quantity. Chef Bill is happy to help estimate
          appropriate quantities once your selections are submitted.
          <br /><br />
          For non-wedding events requesting appetizers only, a <strong>$400 minimum order</strong> applies. Please inquire
          with any questions or special requests.
        </p>

        <QuotePillYesNo value={wantsApps} onYes={() => setWantsApps(true)} onNo={() => setWantsApps(false)} />

        {wantsApps && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {appetizersMenuss.map((m) => (
              <div key={m.name} className="rounded-2xl border border-neutral-200 bg-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-neutral-900">{m.name}</div>
                    <ul className="mt-2 space-y-1 text-sm text-neutral-700 list-disc ml-5">
                      {m.items.map((it) => <li key={it}>{it}</li>)}
                    </ul>
                  </div>
                  <button type="button"
                    onClick={() => toggleApp(m.name)}
                    className={`shrink-0 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      selectedApps.has(m.name) ? "bg-red-600 text-white" : "border border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                    }`}
                  >
                    {selectedApps.has(m.name) ? "Selected" : "Add"}
                  </button>
                </div>

                {selectedApps.has(m.name) && (isDozenItem(m.name) || isMedLgItem(m.name)) && (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {isMedLgItem(m.name) && (
                      <>
                        <div className="text-xs font-semibold text-neutral-700">Size:</div>
                        <select
                          className="rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm"
                          value={(appSize && appSize[m.name]) ? appSize[m.name] : "Med"}
                          onChange={(e) => onSetAppSize && onSetAppSize(m.name, e.target.value)}
                        >
                          <option value="Med">Med</option>
                          <option value="Lg">Lg</option>
                        </select>
                      </>
                    )}

                    <div className="text-xs font-semibold text-neutral-700">{isDozenItem(m.name) ? "Qty (dozen):" : "Qty:"}</div>
                    <select
                      className="rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm"
                      value={(appQty && appQty[m.name]) ? appQty[m.name] : 1}
                      onChange={(e) => onSetAppQty && onSetAppQty(m.name, Number(e.target.value))}
                    >
                      {Array.from({ length: 10 }).map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </QuoteStepShell>
    );
  }

  if (step === 1) {
    const tierPicked = !!dinnerTier;
    const menuPicked = !!dinnerMenuName;

    return (
      <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
        title="Dinner"
        subtitle="Do you want dinner for your party?"
        onBack={() => setStep(0)}
        onNext={() => {
          // validate dinner selections before advancing
          if (wantsDinner === false) { setStep(2); return; }
          if (wantsDinner === null) return;
          const missing = [];
          if (!dinnerTier) {
            tierRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          if (!dinnerMenuName) {
            menuRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          // Tex-Mex menu: ask about queso (Yes/No)
          if (isTexMexMenu(dinnerMenuName) || isTexMexMenu(dinnerConfig?.title)) {
            if (form.texMexQueso === null) {
              setMissingDinner(["texMexQueso"]);
              quesoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
          }
          if (dinnerConfig?.groups?.length) {
            dinnerConfig.groups.forEach((g) => {
              const picks = Array.isArray(dinnerChoices[g.key]) ? dinnerChoices[g.key] : [];
              const limit = g.limit || 99;
              const ok = limit >= 99 ? picks.length >= 1 : picks.length === limit;
              if (!ok) missing.push(g.key);
            });
          }
          if (missing.length) {
            setMissingDinner(missing);
            const first = missing[0];
            const el = dinnerGroupRefs.current[first];
            if (el && typeof el.scrollIntoView === "function") {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
          }
          setMissingDinner([]);
          setStep(2);
        }}
        nextDisabled={wantsDinner === null || (wantsDinner && (!tierPicked || !menuPicked))}
      >
        <QuotePillYesNo value={wantsDinner} onYes={() => setWantsDinner(true)} onNo={() => setWantsDinner(false)} />

        {wantsDinner && (
          <div className="mt-6 space-y-6">
            <div ref={tierRef}>
              <div className="text-sm font-semibold text-neutral-800">Select a tier</div>
              <div className="mt-3 grid md:grid-cols-3 gap-4">
                {dinnerPricingTiers.map((t) => (
                  <QuoteMenuSelectCard
                    key={t.category}
                    title={t.category}
                    subtitle={<div className="space-y-1">{t.rates.map((r) => (<div key={r}>{r}</div>))}</div>}
                    selected={dinnerTier === t.category}
                    onClick={() => { setDinnerTier(t.category); setDinnerMenuName(null); }}
                  />
                ))}
              </div>
            </div>

            {dinnerTier && (
              <div ref={menuRef}>
                <div className="text-sm font-semibold text-neutral-800">Select a dinner menu</div>
                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  {dinnerMenus
                    .filter((m) => (m.name || "").startsWith(`${dinnerTier} •`))
                    .map((m) => (
                      <QuoteMenuSelectCard
                        key={m.name}
                        title={dinnerTitleFromName(m.name)}
                        subtitle="Tap to customize meats, sides, and included options."
                        selected={dinnerMenuName === m.name}
                        onClick={() => setDinnerMenuName(m.name)}
                      />
                    ))}
                </div>
              </div>
            )}

            {dinnerTier && dinnerMenuName && dinnerConfig && (
              <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                <div className="text-lg font-semibold text-neutral-900">{dinnerConfig.title}</div>

                {dinnerConfig.included?.length ? (
                  <div className="mt-3">
                    <div className="text-sm font-semibold text-neutral-800">Included</div>
                    <ul className="mt-2 space-y-1 text-sm text-neutral-700 list-disc ml-5">
                      {dinnerConfig.included.map((it) => <li key={it}>{it}</li>)}
                    </ul>
                  </div>
                ) : null}


                {(isTexMexMenu(dinnerConfig.title) || isTexMexMenu(dinnerMenuName)) && (
                  <div
                    ref={quesoRef}
                    className={`mt-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 ${
                      missingDinner.includes("texMexQueso") ? "ring-2 ring-red-300" : ""
                    }`}
                  >
                    <div className="text-sm font-semibold text-neutral-900">Do you want Queso?</div>
                    <div className="text-xs text-neutral-600">+$35.00 per gallon</div>
                    <QuotePillYesNo
                      value={form.texMexQueso}
                      onYes={() => { setForm((f) => ({ ...f, texMexQueso: true })); setMissingDinner((m) => m.filter((k) => k !== "texMexQueso")); }}
                      onNo={() => { setForm((f) => ({ ...f, texMexQueso: false })); setMissingDinner((m) => m.filter((k) => k !== "texMexQueso")); }}
                    />
                  </div>
                )}

                {dinnerConfig.groups?.length ? (
                  <div className="mt-5 space-y-5">
                    {dinnerConfig.groups.map((g) => {
                      const picks = Array.isArray(dinnerChoices[g.key]) ? dinnerChoices[g.key] : [];
                      const limit = g.limit || 99;
                      const atLimit = picks.length >= limit;

                      return (
                        <div key={g.key} ref={(el) => { if (el) dinnerGroupRefs.current[g.key] = el; }} className={`${missingDinner.includes(g.key) ? "ring-2 ring-red-300 rounded-2xl p-2 -m-2" : ""}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-neutral-900">{g.title}</div>
                              <div className="text-xs text-neutral-600">Choose up to {limit}</div>
                            </div>
                            <div className="text-xs text-neutral-600">{picks.length}/{limit} selected</div>
                          </div>

                          <div className="mt-2 grid sm:grid-cols-2 gap-2">
                            {g.options.map((opt) => {
                              const checked = picks.includes(opt);
                              const disabled = !checked && atLimit;

                              return (
                                <button type="button"
                                  key={opt}
                                  onClick={() => chooseDinner(g.key, opt, limit)}
                                  disabled={disabled}
                                  className={`text-left rounded-xl border px-3 py-2 text-sm transition ${
                                    checked
                                      ? "border-red-600 bg-red-50 text-neutral-900"
                                      : disabled
                                      ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
                                      : "border-neutral-200 hover:bg-neutral-50 text-neutral-800"
                                  }`}
                                >
                                  <span className={`mr-2 inline-block w-4 text-center ${checked ? "text-red-600" : "text-neutral-400"}`}>
                                    {checked ? "✓" : "•"}
                                  </span>
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </QuoteStepShell>
    );
  }


  if (step === 2) {
    return (
      <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
        title="Desserts"
        subtitle="Do you want desserts for your party?"
        onBack={() => setStep(1)}
        onNext={() => setStep(3)}
        nextDisabled={wantsDesserts === null}
      >
   <p className="text-sm text-neutral-700">
          Desserts may be added to any wedding reception menu in any quantity. Chef Bill is happy to help estimate
          appropriate quantities once your selections are submitted.
          <br /><br />
          For non-wedding events requesting desserts only, a <strong>$400 minimum order</strong> applies. Please inquire
          with any questions or special requests.
        </p>
        <QuotePillYesNo value={wantsDesserts} onYes={() => setWantsDesserts(true)} onNo={() => setWantsDesserts(false)} />

        {wantsDesserts && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {dessertsMenus.map((m) => (
              <div key={m.name} className="rounded-2xl border border-neutral-200 bg-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-neutral-900">{m.name}</div>
                    <ul className="mt-2 space-y-1 text-sm text-neutral-700 list-disc ml-5">
                      {m.items.map((it) => <li key={it}>{it}</li>)}
                    </ul>
                  </div>
                  <button type="button"
                    onClick={() => toggleDessert(m.name)}
                    className={`shrink-0 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      selectedDesserts.has(m.name) ? "bg-red-600 text-white" : "border border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                    }`}
                  >
                    {selectedDesserts.has(m.name) ? "Selected" : "Add"}
                  </button>
                </div>

                {isDozenItem(m.name) && selectedDesserts.has(m.name) && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="text-xs font-semibold text-neutral-700">Qty (dozen):</div>
                    <select
                      value={dessertQty[m.name] || 1}
                      onChange={(e) => setDessertQtySafe(m.name, e.target.value)}
                      className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
                    >
                      {Array.from({ length: 10 }).map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}

              </div>
            ))}
          </div>
        )}
      </QuoteStepShell>
    );
  }

  const chosenAppNames = wantsApps ? Array.from(selectedApps) : [];
  const chosenDessertNames = wantsDesserts ? Array.from(selectedDesserts) : [];
  if (step === 5) {

    return (
    <QuoteStepShell onClose={onClose} bodyRef={scrollRef}
      title="Review & Submit"
      subtitle="Confirm your selections and send them to us."
      onBack={() => setStep(4)}
      onNext={onSubmit}
      nextText={isSubmitting ? "Processing..." : "Submit Quote Request"}
      nextDisabled={isSubmitting}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-sm font-semibold text-neutral-900">Contact</div>

          <div className="mt-2 text-sm text-neutral-700 space-y-1">
            <div><span className="font-semibold">Name:</span> {form.name}</div>
            <div><span className="font-semibold">Email:</span> {form.email}</div>
            <div><span className="font-semibold">Phone:</span> {form.phone}</div>
            <div><span className="font-semibold">Date:</span> {form.date}</div>
            <div><span className="font-semibold">Guests:</span> {form.guests}</div>
            <div><span className="font-semibold">Venue:</span> {form.venue}</div>
            <div><span className="font-semibold">Budget:</span> {form.budget}</div>
          </div>

          <div className="border-t border-neutral-200 my-3"></div>

          <div className="text-sm font-semibold text-neutral-900">Services</div>
          <div className="mt-2 text-sm text-neutral-700 space-y-1">
            <div><span className="font-semibold">Service Style:</span> {form.serviceStyle}</div>
            <div><span className="font-semibold">Bartender:</span> {form.bartender ? "Yes" : "No"}</div>
            <div>
              <span className="font-semibold">Drink Service:</span>{" "}
              {form.drinkService
                ? `Yes — ${form.drinkSodas ? "$4.00" : "$2.00"} per person (${(form.drinkOptions || []).filter(Boolean).join(", ") || "TBD"})${form.drinkSodas ? " + Assorted Sodas" : ""}`
                : "No"}
            </div>
            <div>
              <span className="font-semibold">Party Rentals:</span>{" "}
              {form.rentals
                ? `Yes (${[
                    ...(form.rentalOptions || []).filter(Boolean),
                    ...(form.rentalsOther || "").trim() ? [(form.rentalsOther || "").trim()] : [],
                  ].join(", ") || "TBD"})`
                : "No"}
            </div>
            <div>
              <span className="font-semibold">Kids Menu:</span>{" "}
              {form.kidsMenuDiscuss ? `Yes (${Math.max(10, Number(form.kidsCount || 10))} kids)` : "No"}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-sm font-semibold text-neutral-900">Selections</div>

          <div className="mt-3">
            <div className="text-sm font-semibold text-neutral-800">Appetizers</div>
            {wantsApps ? (
              <ul className="mt-2 list-disc ml-5 text-sm text-neutral-700">
                {(chosenAppNames.length ? chosenAppNames : ["(none selected)"]).map((x) => <li key={x}>{x}</li>)}
              </ul>
            ) : (
              <div className="mt-1 text-sm text-neutral-600">No</div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-neutral-800">Dinner</div>
            {wantsDinner ? (
              <div className="mt-2 text-sm text-neutral-700 space-y-2">
                <div><span className="font-semibold">Tier:</span> {dinnerTier || "(not selected)"}</div>
                <div><span className="font-semibold">Menu:</span> {dinnerConfig?.title || "(not selected)"}</div>
                {Array.isArray(dinnerConfig?.included) && dinnerConfig.included.length ? (
                  <div>
                    <div className="font-semibold">Included:</div>
                    <ul className="mt-1 list-disc ml-5">
                      {dinnerConfig.included.map((i) => (
                        <li key={i}>{i}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {(isTexMexMenu(dinnerConfig?.title) || isTexMexMenu(dinnerMenuName)) && (
                  <div>
                    <span className="font-semibold">Queso:</span> {form.texMexQueso === null ? "(not selected)" : (form.texMexQueso ? "Yes" : "No")}
                  </div>
                )}
                {dinnerConfig?.groups?.length ? (
                  <div className="space-y-1">
                    {dinnerConfig.groups.map((g) => {
                      const picks = Array.isArray(dinnerChoices[g.key]) ? dinnerChoices[g.key] : [];
                      return (
                        <div key={g.key} ref={(el) => { if (el) dinnerGroupRefs.current[g.key] = el; }} className={`${missingDinner.includes(g.key) ? "ring-2 ring-red-300 rounded-2xl p-2 -m-2" : ""}`}>
                          <span className="font-semibold">{g.title}:</span>{" "}
                          <span>{picks.length ? picks.join(", ") : "(not selected)"}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-1 text-sm text-neutral-600">No</div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-neutral-800">Desserts</div>
            {wantsDesserts ? (
              <ul className="mt-2 list-disc ml-5 text-sm text-neutral-700">
                {(chosenDessertNames.length ? chosenDessertNames : ["(none selected)"]).map((x) => <li key={x}>{x}</li>)}
              </ul>
            ) : (
              <div className="mt-1 text-sm text-neutral-600">No</div>
            )}
          </div>
        </div>
      </div>

      {form.message?.trim() ? (
        <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="text-sm font-semibold text-neutral-900">Notes</div>
          <div className="mt-2 text-sm text-neutral-700 whitespace-pre-wrap">{form.message.trim()}</div>
        </div>
      ) : null}
    </QuoteStepShell>
  );

  }

  // Step 6: Thank-you / Confirmation
  if (step === 6) {
    const { firstName } = splitName(form.name);
    return (
      <QuoteStepShell
        title="Request Received"
        subtitle=""
        step={6}
        total={6}
        onBack={() => {
          // allow user to go back to review if needed
          setStep(5);
        }}
        backText="Back"
        nextText="Close"
        onNext={() => {
          // reset + close
          setStep(0);
          setForm(initialForm);
          onClose();
        }}
      >
        <div className="rounded-3xl border border-neutral-200 bg-white p-6">
          <div className="text-2xl font-extrabold text-neutral-900">🎉 Thank You{firstName ? `, ${firstName}` : ""}, for Your Event Quote Request! 🍽️🔥</div>
          <p className="mt-3 text-neutral-700">
            We’re excited you reached out to Texas Chef Foods Catering! Your quote request has been successfully received, and our team is already sharpening knives and firing up ideas for your event.
          </p>

          <div className="mt-5 rounded-2xl bg-neutral-50 p-4">
            <div className="font-bold text-neutral-900">⏰ What’s Next?</div>
            <div className="mt-1 text-neutral-700">We’ll be in touch within 48 hours with next steps and delicious details. Please note that weekends are our busiest time (we’re out there making events unforgettable!), so we typically respond Monday through Thursday.</div>
          </div>

          <div className="mt-4 rounded-2xl bg-neutral-50 p-4">
            <div className="font-bold text-neutral-900">📞 Need us sooner?</div>
            <div className="mt-1 text-neutral-700">If you don’t hear back within 48 hours, feel free to reach out directly to Chef Bill at <a className="font-semibold underline" href="tel:9724646883">972-464-6883</a> — he’s always happy to talk food and events.</div>
          </div>

          <div className="mt-5 text-sm text-neutral-600">
            —<br />
            <span className="font-semibold text-neutral-800">Texas Chef Foods Catering</span><br />
            Elevated flavors. Memorable moments.
          </div>
        </div>
      </QuoteStepShell>
    );
  }

  return null;
}


export default function CateringWebsite() {
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuType, setMenuType] = useState("Dinner"); // NEW: Appetizers/Desserts/Dinner toggle
  const [dinnerTier, setDinnerTier] = useState(null); // NEW: selected dinner price category
const [quoteOpen, setQuoteOpen] = useState(false);
const [quoteSubmitting, setQuoteSubmitting] = useState(false);
const [quoteStep, setQuoteStep] = useState(0); // 0 appetizers, 1 dinner, 2 desserts, 3 event details, 4 summary
const [quoteWantsApps, setQuoteWantsApps] = useState(null); // null | true | false
const [quoteWantsDinner, setQuoteWantsDinner] = useState(null);
const [quoteWantsDesserts, setQuoteWantsDesserts] = useState(null);

const [selectedApps, setSelectedApps] = useState(() => new Set());

const [appQty, setAppQty] = useState(() => ({})); // { [appName]: number }
const [appSize, setAppSize] = useState(() => ({})); // { [appName]: "Med" | "Lg" }
const [dessertQty, setDessertQty] = useState(() => ({})); // { [dessertName]: number }
const [selectedDesserts, setSelectedDesserts] = useState(() => new Set());

const [quoteDinnerTier, setQuoteDinnerTier] = useState(null); // Price Category One/Two/Three
const [quoteDinnerMenuName, setQuoteDinnerMenuName] = useState(null); // full dinnerMenus name
const [quoteDinnerChoices, setQuoteDinnerChoices] = useState({}); // { [groupKey]: string[] }

const resetQuote = () => {
  setQuoteStep(0);
    setQuoteSubmitting(false);
setQuoteWantsApps(null);
  setQuoteWantsDinner(null);
  setQuoteWantsDesserts(null);
  setSelectedApps(new Set());
  setAppQty({});
  setAppSize({});
  setDessertQty({});
  setSelectedDesserts(new Set());
  setQuoteDinnerTier(null);
  setQuoteDinnerMenuName(null);
  setQuoteDinnerChoices({});
};

const openQuote = () => {
  resetQuote();
  setQuoteOpen(true);
};

const closeQuote = () => {
  setQuoteOpen(false);
  // Reset so the next open starts clean (and clears the Thank You state)
  resetQuote();
};

const toggleSetItem = (setFn, currentSet, value) => {
  const next = new Set(currentSet);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  setFn(next);
};

// Appetizers: keep quantity for /Dz. items (1-10) in sync with selection
const toggleAppWithQty = (name) => {
  setSelectedApps((prev) => {
    const next = new Set(prev);
    if (next.has(name)) {
      next.delete(name);
      setAppQty((q) => {
        const nq = { ...(q || {}) };
        delete nq[name];
        return nq;
      });
      setAppSize((s) => {
        const ns = { ...(s || {}) };
        delete ns[name];
        return ns;
      });
    } else {
      next.add(name);
      setAppQty((q) => ({ ...(q || {}), [name]: (q && q[name]) ? q[name] : 1 }));
      if (isMedLgItem(name)) {
        setAppSize((s) => ({ ...(s || {}), [name]: (s && s[name]) ? s[name] : "Med" }));
      }
    }
    return next;
  });
};

const setAppQuantity = (name, qty) => {
  const n = Math.max(1, Math.min(10, Number(qty) || 1));
  setAppQty((q) => ({ ...(q || {}), [name]: n }));
};

const setAppSizeChoice = (name, size) => {
  const v = String(size || "Med") === "Lg" ? "Lg" : "Med";
  setAppSize((s) => ({ ...(s || {}), [name]: v }));
};

// Desserts: keep quantity for /Dz. items (1-10) in sync with selection
const toggleDessertWithQty = (name) => {
  setSelectedDesserts((prev) => {
    const next = new Set(prev);
    if (next.has(name)) {
      next.delete(name);
      setDessertQty((q) => {
        const nq = { ...(q || {}) };
        delete nq[name];
        return nq;
      });
    } else {
      next.add(name);
      if (isDozenItem(name)) {
        setDessertQty((q) => ({ ...(q || {}), [name]: (q && q[name]) ? q[name] : 1 }));
      }
    }
    return next;
  });
};

const setDessertQuantity = (name, qty) => {
  const n = Math.max(1, Math.min(10, Number(qty) || 1));
  setDessertQty((q) => ({ ...(q || {}), [name]: n }));
};

// --- Dinner parsing: find "Pick/Choose" groups and their bullet options ---
const parseDinnerMenuConfig = (menu) => {
  if (!menu) return { title: "", included: [], groups: [] };

  const items = Array.isArray(menu.items) ? menu.items : [];
  const groups = [];
  const included = [];

  const normalizeLimit = (header) => {
    const h = (header || "").toLowerCase();
    const d = h.match(/\b(\d+)\b/);
    if (d) return Math.max(1, parseInt(d[1], 10));
    if (h.includes("one")) return 1;
    if (h.includes("two")) return 2;
    if (h.includes("three")) return 3;
    if (h.includes("four")) return 4;
    if (h.includes("five")) return 5;
    return null;
  };

  const isHeader = (s) => ((s || "").trim().endsWith(":"));

  const isGroupHeader = (s) => {
    const t = (s || "").trim();
    if (!isHeader(t)) return false;
    const low = t.toLowerCase();
    return low.includes("pick ") || low.startsWith("pick ") || low.includes("choose ") || low.startsWith("choose ") || low.includes("included appetizer");
  };

  let currentGroup = null;

  items.forEach((raw) => {
    const t = (raw || "").trim();
    const isBullet = t.startsWith("•");
    const clean = isBullet ? t.replace(/^•\s*/, "") : t;

    if (isGroupHeader(clean)) {
      if (currentGroup) groups.push(currentGroup);
      const limit = normalizeLimit(clean) || 99;
      currentGroup = {
        key: `${menu.name}__${clean}`,
        title: clean.replace(/:\s*$/, ""),
        limit,
        options: [],
      };
      return;
    }

    if (isHeader(clean) && !isBullet) {
      if (currentGroup) {
        groups.push(currentGroup);
        currentGroup = null;
      }
      included.push(clean.replace(/:\s*$/, ""));
      return;
    }

    if (isBullet && currentGroup) {
      currentGroup.options.push(clean);
      return;
    }

    if (!isBullet) {
      included.push(clean);
      return;
    }

    included.push(clean);
  });

  if (currentGroup) groups.push(currentGroup);

  return {
    title: dinnerTitleFromName(menu.name),
    included: included.filter(Boolean),
    groups: groups.map((g, idx) => ({ ...g, key: `${g.key}__${idx}` })),
  };
};

const getDinnerMenuByName = (name) => dinnerMenus.find((m) => m.name === name);

const updateDinnerChoice = (groupKey, option, limit) => {
  setQuoteDinnerChoices((prev) => {
    const current = Array.isArray(prev[groupKey]) ? prev[groupKey] : [];
    const has = current.includes(option);
    let next = has ? current.filter((x) => x !== option) : [...current, option];
    if (!has && next.length > limit) next = next.slice(next.length - limit);
    return { ...prev, [groupKey]: next };
  });
};

const buildQuoteEmailBody = () => {
  const lines = [];
  lines.push("=== SERVICE DETAILS ===");
  lines.push(`Service Style: ${form.serviceStyle || ""}`);
  lines.push(`Bartender: ${form.bartender === null ? "" : form.bartender ? "Yes" : "No"}`);
  lines.push(
    `Drink Service: ${form.drinkService === null ? "" : form.drinkService ? "Yes" : "No"}` +
      (form.drinkService
        ? ` — ${form.drinkSodas ? "$4.00" : "$2.00"} per person (${(form.drinkOptions || []).join(", ") || "TBD"})${form.drinkSodas ? " + Assorted Sodas" : ""}`
        : "")
  );
  lines.push(
    `Party Rentals: ${form.rentals === null ? "" : form.rentals ? "Yes" : "No"}` +
      (form.rentals
        ? ` (${(form.rentalOptions || []).filter((x) => x !== "Other").join(", ")}` +
          `${(form.rentalOptions || []).includes("Other")
            ? `${(form.rentalOptions || []).length > 1 ? ", " : ""}Other${form.rentalsOther?.trim() ? `: ${form.rentalsOther.trim()}` : ""}`
            : ""}` +
          `)`
        : "")
  );
  lines.push(
    `Kids Menu: ${form.kidsMenuDiscuss === null ? "" : form.kidsMenuDiscuss ? "Yes" : "No"}` +
      (form.kidsMenuDiscuss ? ` (${Math.max(10, Number(form.kidsCount || 10))} kids)` : "")
  );
  lines.push("");
  lines.push("=== CONTACT ===");
  lines.push(`Name: ${form.name}`);
  lines.push(`Email: ${form.email}`);
  lines.push(`Phone: ${form.phone}`);
  lines.push(`Event Date: ${form.date}`);
  lines.push(`Guest Count: ${form.guests}`);
  lines.push(`Venue Location: ${form.venue}`);
  lines.push(`Party Budget: ${form.budget}`);
  lines.push("");
  lines.push("=== SELECTIONS ===");

  if (quoteWantsApps) {
    lines.push("Appetizers:");
	    Array.from(selectedApps).forEach((a) => {
	      const qty = appQty && appQty[a] ? appQty[a] : 1;
	      const label = isDozenItem(a)
	        ? `Qty: ${qty} Dz. — ${a}`
	        : isMedLgItem(a)
	          ? `Qty: ${qty} ${(appSize && appSize[a]) ? appSize[a] : "Med"} — ${a}`
	          : a;
	      lines.push(`- ${label}`);
	    });
    if (selectedApps.size === 0) lines.push("- (none selected)");
    lines.push("");
  } else {
    lines.push("Appetizers: No");
    lines.push("");
  }

  if (quoteWantsDinner) {
    lines.push("Dinner:");
    if (quoteDinnerTier) lines.push(`- Tier: ${quoteDinnerTier}`);
    if (quoteDinnerMenuName) {
      const menu = getDinnerMenuByName(quoteDinnerMenuName);
      const cfg = parseDinnerMenuConfig(menu);
	      // Avoid dash-splitting downstream by using a non-breaking hyphen.
	      const safeMenuTitle = String(cfg.title || quoteDinnerMenuName).replace(/-/g, "\u2011");
	      lines.push(`- Menu: ${safeMenuTitle}`);

	      // Included items
	      if (Array.isArray(cfg?.included) && cfg.included.length) {
	        lines.push("- Included:");
	        cfg.included.forEach((it) => {
	          if (String(it || "").trim()) lines.push(`  • ${String(it).trim()}`);
	        });
	      }

          // Tex-Mex queso add-on
          if (isTexMexMenu(cfg.title) || isTexMexMenu(quoteDinnerMenuName)) {
            if (form.texMexQueso !== null) {
              lines.push(`- Queso: ${form.texMexQueso ? "Yes" : "No"}`);
            }
          }

	      cfg.groups.forEach((g) => {
	        const picks = Array.isArray(quoteDinnerChoices[g.key]) ? quoteDinnerChoices[g.key] : [];
	        if (!picks.length) return;
	        lines.push(`- ${g.title}: ${picks.join(", ")}`);
	      });
    } else {
      lines.push("- (no dinner menu selected)");
    }
    lines.push("");
  } else {
    lines.push("Dinner: No");
    lines.push("");
  }

  if (quoteWantsDesserts) {
    lines.push("Desserts:");
    Array.from(selectedDesserts).forEach((d) => {
      const qty = dessertQty && dessertQty[d] ? dessertQty[d] : 1;
	      const label = isDozenItem(d) ? `Qty: ${qty} Dz. — ${d}` : d;
      lines.push(`- ${label}`);
    });
    if (selectedDesserts.size === 0) lines.push("- (none selected)");
    lines.push("");
  } else {
    lines.push("Desserts: No");
    lines.push("");
  }

  if ((form.message || "").trim()) {
    lines.push("=== NOTES ===");
    lines.push(form.message.trim());
  }

  return lines.join("\n");
};

			const submitQuote = async () => {
			  setQuoteSubmitting(true);
			  // Build payload for your Google Apps Script (DOCX + Email)
			  const { firstName, lastName } = splitName(form.name);

	  // 1) Add the contact to Mailchimp automatically
	  //    (hidden iframe form submit to avoid CORS + no user redirect)
      // Add contact to Mailchimp automatically (silent fail)
      try {
        subscribeToMailchimp({
          email: form.email,
          firstName,
          lastName,
          phone: form.phone,
        });
      } catch (e) {
        // Ignore Mailchimp errors so quote submission still succeeds
      }
		  // Resolve selected dinner menu object and its config
		  const dinnerMenuObj = quoteDinnerMenuName
		    ? dinnerMenus.find((m) => String(m.name || "") === String(quoteDinnerMenuName))
		    : null;
		  const dinnerCfg = dinnerMenuObj ? parseDinnerMenuConfig(dinnerMenuObj) : null;

	  const selectionsPayload = [];
	  if (quoteWantsApps) {
	    selectionsPayload.push({
	      title: "Appetizers",
	      items: Array.from(selectedApps).map((name) => {
        const qty = (appQty && appQty[name]) ? appQty[name] : 1;
        if (isDozenItem(name)) return `Qty: ${qty} Dz. — ${name}`;
        if (isMedLgItem(name)) {
          const size = (appSize && appSize[name]) ? appSize[name] : "Med";
          return `Qty: ${qty} ${size} — ${name}`;
        }
        return name;
      }),
	    });
	  }
	  if (quoteWantsDinner) {
	    const dinnerItems = [];
		    // Menu name (do NOT append tier here; tier is handled once via meta.tier and/or the script)
		    if (quoteDinnerMenuName) {
		      // Avoid dash-splitting in downstream doc generation by using a non-breaking hyphen for menu names.
		      const safeMenuName = String(quoteDinnerMenuName).replace(/-/g, "\u2011");
		      dinnerItems.push(`Menu: ${safeMenuName}`);
		    }
		    // Included items (show in Summary/Email/DOCX)
		    if (Array.isArray(dinnerCfg?.included) && dinnerCfg.included.length) {
		      dinnerItems.push("Included:");
		      dinnerCfg.included.forEach((it) => {
		        if (String(it || "").trim()) dinnerItems.push(`• ${String(it).trim()}`);
		      });
		    }

        // Tex-Mex queso add-on
        if (isTexMexMenu(dinnerCfg?.title) || isTexMexMenu(quoteDinnerMenuName)) {
          if (form.texMexQueso !== null) {
            dinnerItems.push(`Queso: ${form.texMexQueso ? "Yes" : "No"}`);
          }
        }
		    // Expand all selected dinner details
		    if (dinnerCfg?.groups?.length) {
		      dinnerCfg.groups.forEach((g) => {
		        const picks = Array.isArray(quoteDinnerChoices[g.key]) ? quoteDinnerChoices[g.key] : [];
		        if (!picks.length) return;
		        dinnerItems.push(`${g.title}: ${picks.join(", ")}`);
		      });
		    }
	    selectionsPayload.push({
	      title: "Dinner",
	      items: dinnerItems,
	    });
	  }
	  if (quoteWantsDesserts) {
	    selectionsPayload.push({
	      title: "Desserts",
	      items: Array.from(selectedDesserts).map((d) => {
        const qty = dessertQty && dessertQty[d] ? dessertQty[d] : 1;
	        return isDozenItem(d) ? `Qty: ${qty} Dz. — ${d}` : d;
      }),
	    });
	  }

	  const payload = {
	    contact: {
	      name: String(form.name || "").trim(),
	      firstName,
	      lastName,
	      email: form.email || "",
	      phone: form.phone || "",
	      eventDate: form.date || "",
	      guestCount: form.guests || "",
	      venue: form.venue || "",
	      budget: form.budget || "",
	      message: form.message || "",
	    },
	    services: {
	      style: form.serviceStyle || "",
	      bartender: !!form.bartender,
	      drinkService: !!form.drinkService,
	      drinks: Array.isArray(form.drinkOptions) ? form.drinkOptions : [],
	      drinkSodas: !!form.drinkSodas,
	      rentals: Array.isArray(form.rentalOptions) ? form.rentalOptions : [],
	      kidsMenu: !!form.kidsMenuDiscuss,
	      kidsCount: form.kidsMenuDiscuss ? Math.max(10, Number(form.kidsCount || 10)) : 0,
	    },
	    selections: selectionsPayload,
	    meta: {
	      tier: quoteDinnerTier || "",
	    },
	  };

	  try {
	    // `no-cors` still sends the request even if we can't read the response.
	    await fetch(GOOGLE_SCRIPT_URL, {
	      method: "POST",
	      mode: "no-cors",
	      headers: { "Content-Type": "text/plain;charset=utf-8" },
	      body: JSON.stringify(payload),
	    });
	  } catch (err) {
	    console.error("Quote submit failed:", err);
	  }

	  // Show the confirmation page inside the wizard
	  setQuoteStep(6);
	  setQuoteSubmitting(false);
};

  // Smooth scroll handler used by all in-page links
  const smoothScroll = (e, hash) => {
    if (e) e.preventDefault();
    const id = typeof hash === "string" ? hash : (e?.currentTarget?.getAttribute("href") || "");
    const el = id ? document.querySelector(id) : null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      if (window?.history?.pushState) window.history.pushState(null, "", id);
    }
    setMobileOpen(false);
  };

  const headingAnim = { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5 }, viewport: { once: true, margin: "-80px" } };
  const cardAnim = (i=0) => ({ initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: i*0.06 }, viewport: { once: true } });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent("New Event Inquiry");
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nEvent Date: ${form.date}\nGuest Count: ${form.guests}\nVenue Location: ${form.venue}\nParty Budget: ${form.budget}\n\nMessage:\n${form.message}`
    );
    window.location.href = `mailto:${BRAND.email}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  // NEW: Inquire button logic - prefill the message and jump to contact
  const handleInquire = (menuName) => {
    setForm((f) => ({ ...f, message: `Interested in: ${menuName}` }));
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Compute visible menus and the max bullet count among them to equalize heights
  const visibleMenus =
    menuType === "Appetizers" ? appetizersMenuss :
    menuType === "Desserts" ? dessertsMenus :
    (dinnerTier ? dinnerMenus.filter((m) => (m.name || "").startsWith(`${dinnerTier} •`)) : []);
  const maxItems = visibleMenus.length ? Math.max(...visibleMenus.map((m) => m.items.length)) : 0;

  const dinnerTitleFromName = (name = "") => {
    // "Price Category One • Tex—Mex Menu" -> "Tex—Mex Menu"
    const parts = name.split("•");
    if (parts.length < 2) return name;
    const afterTier = parts[1].trim();
    return afterTier.split("-")[0].trim();
  };

  const isSubBullet = (s = "") => s.trim().startsWith("•");

return (
    <>
    <div id="home" className="min-h-screen bg-neutral-50 text-neutral-900">

{/* Quote Wizard Modal */}
<QuoteWizardModal
  open={quoteOpen}
  onClose={closeQuote}
  step={quoteStep}
  setStep={setQuoteStep}
  form={form}
  setForm={setForm}
  wantsApps={quoteWantsApps}
  setWantsApps={setQuoteWantsApps}
  wantsDinner={quoteWantsDinner}
  setWantsDinner={setQuoteWantsDinner}
  wantsDesserts={quoteWantsDesserts}
  setWantsDesserts={setQuoteWantsDesserts}
  appetizersMenuss={appetizersMenuss}
  dessertsMenus={dessertsMenus}
  dinnerPricingTiers={dinnerPricingTiers}
  dinnerMenus={dinnerMenus}
  dinnerTier={quoteDinnerTier}
  setDinnerTier={setQuoteDinnerTier}
  dinnerMenuName={quoteDinnerMenuName}
  setDinnerMenuName={setQuoteDinnerMenuName}
  dinnerConfig={parseDinnerMenuConfig(getDinnerMenuByName(quoteDinnerMenuName))}
  dinnerChoices={quoteDinnerChoices}
  onToggleApp={toggleAppWithQty}
  onToggleDessert={toggleDessertWithQty}
  selectedApps={selectedApps}
    appQty={appQty}
  appSize={appSize}
  onSetAppQty={setAppQuantity}
  onSetAppSize={setAppSizeChoice}
  dessertQty={dessertQty}
  onSetDessertQty={setDessertQuantity}
  isSubmitting={quoteSubmitting}
selectedDesserts={selectedDesserts}
  onDinnerChoice={updateDinnerChoice}
  onSubmit={submitQuote}
/>
      {/* Top Bar */}
      <div className="w-full" style={{ background: BRAND.colors.bg }}>
        <div className="mx-auto max-w-7xl px-4 py-2 text-white text-sm flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="hidden md:inline-flex items-center gap-2"><MapPin className="w-4 h-4"/> {BRAND.address}</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:${BRAND.phoneHref}`} className="inline-flex items-center gap-2 hover:text-red-400"><Phone className="w-4 h-4"/>{BRAND.phone}</a>
            <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 hover:text-red-400"><Mail className="w-4 h-4"/>{BRAND.email}</a>
          </div>
        </div>
      </div>

      {/* Header / Nav */}
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-0 py-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Texas Chef Foods Catering" className="h-32 md:h-36 lg:h-40 w-auto object-contain" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10 ml-6">
            {navItems.map((n) => (
              <a key={n.href} href={n.href} onClick={(e)=>smoothScroll(e,n.href)} className="text-neutral-700 hover:text-neutral-900">
                {n.label}
              </a>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden inline-flex items-center justify-center rounded-xl border px-3 py-2"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>

          <button type="button" onClick={openQuote} className="ml-3 hidden md:inline-flex items-center rounded-xl border border-red-600 text-red-600 px-4 py-2 hover:bg-red-600 hover:text-white transition">Get a Quote</button>
         <a href={PAY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-xl bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition">Pay Invoice</a>
        </div>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t bg-white/95 backdrop-blur">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-2">
              {navItems.map((n) => (
                <a key={n.href} href={n.href} onClick={(e)=>smoothScroll(e,n.href)} className="py-2 text-neutral-800">
                  {n.label}
                </a>
              ))}
              <button type="button" onClick={() => { setMobileOpen(false); openQuote(); }} className="mt-2 inline-flex items-center justify-center rounded-xl border border-red-600 text-red-600 px-4 py-2 hover:bg-red-600 hover:text-white transition">Get a Quote</button>
              <a href={PAY_URL} onClick={()=>setMobileOpen(false)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center justify-center rounded-xl bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition">Pay Invoice</a>
            </div>
          </div>
        )}
      </header>

      {/* HERO (Dark) */}
      <div className="bg-neutral-950 text-neutral-50">
        <HeroVideoScroller onRequestQuote={openQuote} />
      </div>

      {/* Services (Dark) - appears immediately after hero */}
      <Section id="services" intent="dark">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div {...headingAnim}>
            <h2 className="text-3xl md:text-4xl font-bold">Services</h2>
            <p className="mt-3 text-neutral-300">Flexible options to match your vision, venue, and budget.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {services.map((s, idx) => (
              <motion.div key={s.title} {...cardAnim(idx)}>
                <div className="h-full rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
                  <div className="flex items-start gap-4">
                    <s.icon className="w-10 h-10 text-red-500" />
                    <div>
                      <h3 className="text-xl font-semibold">{s.title}</h3>
                      <p className="mt-2 text-neutral-300">{s.text}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Menus (Light) */}
      <Section id="menus">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div {...headingAnim}>
            <h2 className="text-3xl md:text-4xl font-bold">Menus</h2>
            <p className="mt-3 text-neutral-700">We tailor every menu to your event-these are a few popular starting points. Toggle between Appetizers, Desserts, and Dinner packages below.</p>
          </motion.div>

          
{/* NEW: Appetizers/Desserts/Dinner Toggle + Get A Quote */}
<div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
  <div className="inline-flex rounded-xl border border-neutral-200 p-1 bg-white" role="tablist" aria-label="Menu type">
    {(["Appetizers", "Desserts", "Dinner"]).map((t) => (
      <button
        key={t}
        role="tab"
        aria-selected={menuType===t}
        onClick={() => { setMenuType(t); if (t !== "Dinner") setDinnerTier(null); }}
        className={`px-4 py-2 rounded-lg text-sm md:text-base transition ${menuType===t ? 'bg-red-600 text-white' : 'text-neutral-700 hover:bg-neutral-100'}`}
      >
        {t}
      </button>
    ))}
  </div>

  <button
    onClick={openQuote}
    className="inline-flex items-center justify-center rounded-xl bg-red-600 text-white px-5 py-2.5 hover:bg-red-700 transition font-semibold"
  >
    Click Here to Build Your Event Menu And Get a Quote!
  </button>
</div>

          {/* Dinner pricing (shown only on Dinner tab) */}
          {menuType === "Dinner" && (
            <div className="mt-6">
              <div className="grid md:grid-cols-3 gap-4">
                {dinnerPricingTiers.map((tier) => (
                  <div
                    key={tier.category}
                    className={`rounded-2xl border border-neutral-200 bg-white p-5 ${dinnerTier === tier.category ? "ring-2 ring-red-600" : ""}`}
                  >
                    <div className="flex flex-col h-full">
  <div className="flex-1">
    <div className="font-semibold text-neutral-900">{tier.category}</div>
    <ul className="mt-2 space-y-1 text-sm text-neutral-700">
      {tier.rates.map((r) => (
        <li key={r} className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-red-600" /> {r}
        </li>
      ))}
    </ul>
  </div>

  <div className="pt-4 flex justify-center">
    <button
      onClick={() => setDinnerTier(tier.category)}
      className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
    >
      View menus
    </button>
  </div>
</div>
                  </div>
                ))}
              </div>

              {dinnerTier && (
                <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="text-sm text-neutral-700">
                    Showing menus for <span className="font-semibold text-neutral-900">{dinnerTier}</span>
                  </div>
                  <button
                    onClick={() => setDinnerTier(null)}
                    className="text-sm font-semibold text-red-700 hover:text-red-800 underline underline-offset-4"
                  >
                    Change category
                  </button>
                </div>
              )}

              {!dinnerTier && (
                <div className="mt-4 text-sm text-neutral-600">
                  Choose a price category above to view the menus.
                </div>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {visibleMenus.map((m, idx) => (
              <motion.div key={`${menuType}-${m.name}`} {...cardAnim(idx)}>
                <div className="h-full rounded-2xl border border-neutral-200 bg-white p-6 flex flex-col">
                  <h3 className="text-xl font-semibold">{menuType === "Dinner" ? dinnerTitleFromName(m.name) : m.name}</h3>
                  {/* Equal-height bullet list */}
                  <ul className="mt-3 space-y-2 flex-1">
                    {m.items.map((it) => {
                      const sub = menuType === "Dinner" && isSubBullet(it);
                      const clean = sub ? it.replace(/^•\s*/, "") : it;
                      const isHeader = !sub && /:\s*$/.test(clean);

                      // Dinner menus should read like a menu (no icons, consistent sizing, clear hierarchy)
                      if (menuType === "Dinner") {
                        return (
                          <li
                            key={it}
                            className={
                              isHeader
                                ? "mt-3 font-semibold text-neutral-900"
                                : sub
                                ? "ml-5 list-disc text-neutral-700"
                                : "text-neutral-700"
                            }
                          >
                            {clean}
                          </li>
                        );
                      }

                      // Appetizers/Desserts keep the checklist-style rows with icons
                      return (
                        <li key={it} className="flex items-start gap-2 text-neutral-700">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 text-red-600" />
                          <span className={isHeader ? "font-semibold text-neutral-900" : ""}>{clean}</span>
                        </li>
                      );
                      })}{/* Pad with invisible items to align card heights */}
                    {Array.from({ length: Math.max(0, maxItems - m.items.length) }).map((_, i) => (
                      <li key={`pad-${i}`} className="flex items-start gap-2 opacity-0 select-none">
                        <CheckCircle2 className="w-5 h-5 mt-0.5" /> &nbsp;
                      </li>
                    ))}
                  </ul>
                  
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-neutral-600 mt-4">Need something specific? We love custom requests-regional cuisines, late-night snacks, kids’ menus, and more.</p>
        </div>
      </Section>


{/* Gallery (Dark) */}
<Section id="gallery" intent="dark">
  <div className="mx-auto max-w-7xl px-4">
    <motion.div {...headingAnim}>
      <h2 className="text-3xl md:text-4xl font-bold">Gallery</h2>
      <p className="mt-3 text-neutral-300">
        A peek at recent events and favorite dishes.
      </p>
    </motion.div>

    <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-6">
 {[
  "/gallery/01.png",
  "/gallery/02.png",
  "/gallery/03.png",
  "/gallery/04.png",
  "/gallery/05.png",
  "/gallery/06.png",
  "/gallery/11.png",
  "/gallery/08.png",
  "/gallery/09.png",
  "/gallery/10.png",
  "/gallery/07.png",
  "/gallery/12.png",
].map((src, idx) => (
  <motion.img
    key={src}
    {...cardAnim(idx)}
    loading="lazy"
    className="h-56 w-full object-cover rounded-xl ring-1 ring-neutral-800"
    src={src}
    alt="Texas Chef Foods catering gallery"
    onError={(e) => {
      e.currentTarget.style.opacity = 0.25;
      e.currentTarget.title = `Missing image: ${src} (check public/gallery/)`;
    }}
  />
))}
    </div>
  </div>
</Section>


      {/* Testimonials (Light) - hard-coded */}
      <Section id="testimonials">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div {...headingAnim}>
            <h2 className="text-3xl md:text-4xl font-bold">Kind Words</h2>
            <p className="mt-3 text-neutral-700">We measure success in smiles, empty plates, and stress-free hosts.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {testimonialsFallback.map((t, idx) => (
              <motion.div key={t.name} {...cardAnim(idx)}>
                <div className="rounded-2xl border border-neutral-200 bg-white p-6">
                  <p className="text-neutral-800">“{t.quote}”</p>
                  <div className="mt-4 text-sm text-neutral-600">- {t.name}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ (Dark) */}
      <Section id="faq" intent="dark">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div {...headingAnim}>
            <h2 className="text-3xl md:text-4xl font-bold">FAQ</h2>
            <p className="mt-3 text-neutral-300">If you don’t see your question here, we’re one message away.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {faqs.map((f, idx) => (
              <motion.div key={f.q} {...cardAnim(idx)}>
                <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
                  <h3 className="font-semibold text-white">{f.q}</h3>
                  <p className="mt-2 text-neutral-300">{f.a}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Contact (Light) */}
      <Section id="contact">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <motion.div {...cardAnim(0)}>
              <h2 className="text-3xl md:text-4xl font-bold">Let’s cook up something great</h2>
              <p className="mt-3 text-neutral-700">Tell us about your event and we’ll get you a custom proposal within 24 hours.</p>
              <div className="mt-6 space-y-3 text-neutral-700">
                <div className="flex items-center gap-2"><Phone className="w-5 h-5 text-red-600"/><a href={`tel:${BRAND.phoneHref}`} className="hover:underline">{BRAND.phone}</a></div>
                <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-red-600"/><a href={`mailto:${BRAND.email}`} className="hover:underline">{BRAND.email}</a></div>
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-red-600"/>{BRAND.address}</div>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <a href="https://www.instagram.com/texascheffoods" target="_blank" rel="noreferrer" aria-label="Instagram" className="inline-flex items-center gap-2 hover:text-red-600"><Instagram className="w-5 h-5"/> Instagram</a>
                <a href="https://www.facebook.com/texascheffoods" target="_blank" rel="noreferrer" aria-label="Facebook" className="inline-flex items-center gap-2 hover:text-red-600"><Facebook className="w-5 h-5"/> Facebook</a>
              </div>
            </motion.div>

            <motion.div className="rounded-2xl border border-neutral-200 bg-white p-6" {...cardAnim(0)}>
              {sent ? (
                <div className="text-center py-10">
                  <h3 className="text-xl font-semibold mb-2">Inquiry Prepared</h3>
                  <p className="text-neutral-700">Your email client should open shortly. If it didn’t, email us directly at <a className="underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1">Name</label>
                      <input required value={form.name} onChange={(e)=>setForm((prev)=>({ ...prev, name: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Your name" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Email</label>
                      <input type="email" required value={form.email} onChange={(e)=>setForm((prev)=>({ ...prev, email: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="you@example.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Phone</label>
                    <input type="tel" required value={form.phone} onChange={(e)=>setForm((prev)=>({ ...prev, phone: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="(###) ###-####" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1">Event Date</label>
                      <input type="date" required value={form.date} onChange={(e)=>setForm((prev)=>({ ...prev, date: e.target.value }))} className="w-full rounded-xl border px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Guest Count</label>
                      <input type="number" min="1" required value={form.guests} onChange={(e)=>setForm((prev)=>({ ...prev, guests: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="e.g., 150" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1">Venue Location</label>
                      <input required value={form.venue} onChange={(e)=>setForm((prev)=>({ ...prev, venue: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Venue name & city" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Party Budget</label>
                      <input required value={form.budget} onChange={(e)=>setForm((prev)=>({ ...prev, budget: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="$2,000 - $15,000+" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Message</label>
                    <textarea rows={4} value={form.message} onChange={(e)=>setForm((prev)=>({ ...prev, message: e.target.value }))} className="w-full rounded-xl border px-3 py-2" placeholder="Tell us about your venue, timing, style, and any dietary needs..." />
                  </div>
                  <button className="w-full rounded-xl bg-red-600 text-white px-4 py-3 hover:bg-red-700 transition" type="submit">Send Inquiry</button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="mt-24 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 py-10 md:py-14 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={BRAND.logo} alt="Logo" className="h-14 w-auto" />
            </div>
            <p className="text-neutral-600 max-w-sm">Elevated catering for weddings, corporate events, and private celebrations across North Texas.</p>
          </div>
          <div>
            <div className="font-semibold mb-3">Quick Links</div>
            <ul className="space-y-2">
              {navItems.map(n => (
                <li key={n.href}><a className="hover:text-red-600" href={n.href} onClick={(e)=>smoothScroll(e,n.href)}>{n.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3">Contact</div>
            <ul className="space-y-2 text-neutral-600">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-red-600"/><a className="hover:underline" href={`tel:${BRAND.phoneHref}`}>{BRAND.phone}</a></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-red-600"/><a className="hover:underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a></li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-red-600"/>{BRAND.address}</li>
            </ul>
          </div>
        </div>
        <div className="text-center text-xs text-neutral-500 pb-8">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</div>
      </footer>
    </div>
    </>
  );
}